import { create } from 'zustand';

// Translation keys for product card and details
import { persist } from 'zustand/middleware';

export type Language = 'he' | 'en' | 'ar' | 'ru';

interface LanguageStore {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  he: {
    // Header
    'nav.home': 'ראשי',
    'nav.products': 'מוצרים',
    'nav.categories': 'קטגוריות',
    'nav.about': 'אודות',
    'nav.contact': 'צור קשר',
    'search.placeholder': 'חיפוש מוצרים...',
    'search.noResults': 'לא נמצאו תוצאות',

    // Categories
    'categories.title': 'קטגוריות',
    'categories.description': 'בחרו קטגוריה לצפייה במוצרים',
    'categories.viewProducts': 'צפה במוצרים',
    'categories.screwdrivers': 'מברגות וביטים',
    'categories.screwdriversDesc': 'מברגות ידניות, חשמליות וסטי ביטים מקצועיים',
    'categories.sets': 'סטים מקצועיים',
    'categories.setsDesc': 'ערכות כלים משולבות לכל מלאכה',
    'categories.handtools': 'כלי יד',
    'categories.handtoolsDesc': 'פטישים, מפתחות, צבתים וכלים ידניים',
    'categories.accessories': 'אביזרים',
    'categories.accessoriesDesc': 'שקיות, ארגזים וחלקי חילוף',
    'categories.measuring': 'כלי מדידה',
    'categories.measuringDesc': 'סרגלים, פלסים ומכשירי מדידה',
    'categories.home': 'בית',
    'categories.homeDesc': 'מוצרים שימושיים לבית ולמשפחה',

    // Hero
    'hero.title': 'כלי עבודה מקצועיים',
    'hero.subtitle': 'איכות ללא פשרות',
    'hero.cta': 'לכל המוצרים',

    'hero.badge': 'מוצרים איכותיים במחירים משתלמים',
    'hero.headlineLead': 'עבודות נגרות',
    'hero.headlineAccent': 'בהתאמה אישית',
    'hero.headlineTail': '',
    'hero.description': 'שולחנות, שידות, מזנונים, קונסולות, שרפרפים ועוד — הכל מעץ מלא, בעבודת יד מקצועית ובמידות שמתאימות בדיוק לכם.',
    'hero.badge1': 'עץ מלא',
    'hero.badge2': 'עבודת יד',
    'hero.badge3': 'מידות בהתאמה',
    'hero.primaryCta': 'צפו בעבודות שלנו',
    'hero.secondaryCta': 'צור קשר',

    'hero.feature1Title': 'עיצוב אישי',
    'hero.feature1Desc': 'כל פריט נבנה לפי הצרכים שלכם',
    'hero.feature2Title': 'עבודת יד',
    'hero.feature2Desc': 'מעץ מלא, עם גימור מקצועי',

    // Home
    'home.featuredTitle': 'העבודות שלנו',
    'home.featuredSubtitle': 'הפרויקטים האחרונים',
    'home.featuredBadge': 'פורטפוליו',
    'home.featuredDescription': 'שולחנות, שידות, מזנונים וקונסולות — כל פריט נבנה בעבודת יד מעץ מלא בהתאמה אישית',
    'home.viewAllProducts': 'לכל העבודות',

    // Carpentry section on Index
    'home.contactCta': 'ליצירת קשר',
    'home.portfolioBadge': '✦ פורטפוליו עבודות ✦',
    'home.carpentryTitle': 'עבודות נגרות',
    'home.carpentryAccent': 'בהתאמה אישית',
    'home.carpentryDesc': 'שולחנות, שידות, מזנונים, קונסולות, שרפרפים ועוד — הכל מעץ מלא ובעבודת יד.',
    'home.carpentryDescAccent': 'ספרו לנו מה אתם מחפשים ואנחנו נבנה את זה.',
    'home.tag1': 'עץ מלא 100%',
    'home.tag2': 'עבודת יד',
    'home.tag3': 'צבע לבחירתכם',
    'home.tag4': 'מידות בהתאמה',
    'home.labelOutdoorTable': 'שולחן חוץ',
    'home.labelTvCabinet': 'מזנון',
    'home.labelVanity': 'שידת כיור',
    'home.labelShelf': 'מדף',
    'home.labelCoffeeTable': 'שולחן סלון',
    'home.labelGlassCabinet': 'ארון ויטרינה',
    'home.labelConsole': 'קונסולה',
    'home.beforeAfterTitle': 'חידוש ושיפוץ —',
    'home.beforeAfterAccent': 'לפני ואחרי',
    'home.tableRenovation': 'חידוש שולחן',
    'home.deckRenovation': 'חידוש דק',
    'home.viewAllWorks': 'צפו בכל העבודות שלנו',

    // Nav links
    'nav.carpentry': 'הנגרות שלנו',
    'nav.woodTable': 'שולחן עץ חוץ',

    // Footer
    'footer.ourWorks': 'העבודות שלנו',
    'footer.outdoorTable': 'שולחן חוץ',
    
    // Featured section
    'featured.trending': 'מוצרים פופולריים',
    'featured.quality': 'איכות מעולה',
    'featured.fastDelivery': 'משלוח לכל הארץ',
    'featured.spotlight': 'מוצר מומלץ',
    
    // Car Cover Video Section
    'carCover.badge': 'מוצר הדגל שלנו',
    'carCover.videoTitle': 'כיסוי רכב חשמלי אוטומטי',
    'carCover.videoDescription': 'צפו באיך הכיסוי נפתח ונסגר באופן אוטומטי ומגן על הרכב שלכם בכל מזג אוויר',
    'carCover.viewProduct': 'צפה במוצר המלא',
    'carCover.bestseller': 'הכי נמכר',
    'carCover.feature1': 'פתיחה אוטומטית בשלט',
    'carCover.feature2': 'הגנה מפני שמש וגשם',
    'carCover.feature3': 'התקנה קלה ומהירה',
    'carCover.feature4': '12 וריאציות זמינות',
    
    // Products view
    'products.viewProduct': 'צפה במוצר',
    'home.viewAll': 'צפה בכל המוצרים',
    'home.viewAllCategories': 'צפה בכל הקטגוריות',
    'home.ctaTitle': 'רוצים רהיט בהתאמה אישית?',
    'home.ctaDescription': 'ספרו לנו מה אתם מחפשים ואנחנו נבנה את זה — שולחנות, שידות, מזנונים, קונסולות ועוד. הכל מעץ מלא ובעבודת יד.',
    'home.ctaButton': 'דברו איתנו',
    'home.contactComfort': 'חשוב לנו שתרגישו בנוח',
    'home.contactAvailable': 'אנחנו זמינים לכל שאלה בשמחה',
    'home.contactUs': 'צרו קשר',

    // Header
    'header.promoBanner': '✦ נשמח לתת מענה לכל שאלה – תרגישו בבית ✦',

    // Navigation
    'navigation.back': 'חזרה',
    'navigation.forward': 'קדימה',

    // Referral
    'referral.title': '🎁 חבר מביא חבר - קבלו 30₪ הנחה!',
    'referral.step1': 'הזמינו חבר להירשם עם הקוד שלכם',
    'referral.step2': 'החבר מבצע רכישה ומזין את הקוד בעגלה',
    'referral.step3': 'החבר כותב ביקורת 5 כוכבים',
    'referral.reward': '✨ אתם מקבלים קופון 30₪ ישירות באתר!',
    'referral.myCode': 'לקוד ההפניה שלי',
    'product.optionsNotAvailable': 'צירוף האופציות שנבחר אינו זמין',
    'product.selectOtherOptions': 'אנא בחרו צירוף אחר',

    // Products
    'products.title': 'המוצרים שלנו',
    'products.description': 'מבחר גדול של כלי עבודה מקצועיים במחירים הטובים ביותר בארץ. קנייה נוחה עם משלוח לכל הארץ.',
    'products.addToCart': 'הוסף לסל',
    'products.writeReview': 'כתוב ביקורת',
    'products.viewDetails': 'פרטים נוספים',
    'products.price': 'מחיר',
    'products.includesVat': 'כולל מע"מ',
    'products.outOfStock': 'אזל מהמלאי',
    'products.clearFilter': 'נקה סינון',
    'products.hotDeals': '🔥 מבצעים חמים 🔥',
    'products.startingFrom': 'החל מ-',
    'products.selectOptions': 'בחר אופציות',
    'products.selectOptionsToast': 'כדי לקבל מחיר מדויק יש לבחור אופציות (צבע/מידה)',
    'products.savings': 'חיסכון של',
    'products.premium': 'פרימיום',
    'products.reviews': 'ביקורות',
    'products.viewOptions': 'צפה באפשרויות',
    'product.allDetails': 'כל הפרטים',

    // Product grid
    'productGrid.emptyTitle': 'אין מוצרים להצגה',
    'productGrid.emptyDescription': 'עדיין לא נוספו מוצרים לחנות. ספר לנו מה תרצה למכור ונוסיף את המוצרים עבורך.',
    'productGrid.loadMore': 'טען עוד מוצרים',
    'productGrid.loading': 'טוען...',
    'productGrid.showingProducts': 'מציג {count} מוצרים',
    'productGrid.endOfCatalog': 'הגעת לסוף הקטלוג',
    'productGrid.moreToDiscover': 'יש עוד מוצרים מעולים לגלות',

    // Product detail
    'product.notFound': 'מוצר לא נמצא',
    'product.backToProducts': 'חזרה למוצרים',
    'product.inStock': 'במלאי',
    'product.descriptionTitle': 'תיאור המוצר',
    'product.options': 'אפשרויות',
    'product.quantity': 'כמות',
    'product.shippingTitle': 'משלוח לכל הארץ',
    'product.shippingSubtitle': 'המוצרים נשלחים אליכם',
    'product.fastShipping': 'משלוח לכל הארץ',
    'product.shippingDays': 'עד הבית שלכם',
    'product.securePayment': 'תשלום מאובטח',
    'product.sslEncrypted': 'SSL מוצפן',
    'product.bestPrice': 'מחיר מנצח',
    'product.winningPrice': 'הכי זול בארץ',
    'product.personalService': 'שירות אישי',
    'product.fastSupport': 'תמיכה מהירה',

    // Cart
    'cart.title': 'עגלת קניות',
    'cart.empty': 'העגלה שלך ריקה',
    'cart.emptyPrompt': 'הוסף מוצרים לעגלה כדי להתחיל',
    'cart.added': 'נוסף לעגלה',
    'cart.total': 'סה"כ',
    'cart.checkout': 'מעבר לתשלום',
    'cart.processing': 'מעבד...',
    'cart.items': 'פריטים בעגלה',
    'cart.referralCode': 'קוד הפניה',
    'cart.referralCodePlaceholder': 'הזן קוד הפניה מחבר',
    'cart.validCode': 'קוד תקין!',
    'cart.invalidCode': 'קוד לא נמצא',
    'cart.redirecting': 'מעביר לתשלום...',
    'cart.checkoutError': 'שגיאה במעבר לתשלום',

    // Contact
    'contact.title': 'צור קשר',
    'contact.subtitle': 'יש לכם שאלה? רוצים לדעת עוד על המוצרים שלנו? אנחנו כאן בשבילכם ונשמח לעזור.',
    'contact.formTitle': 'שלחו לנו הודעה',
    'contact.infoTitle': 'פרטי התקשרות',
    'contact.name': 'שם מלא',
    'contact.namePlaceholder': 'השם שלך',
    'contact.email': 'אימייל',
    'contact.phone': 'טלפון',
    'contact.subject': 'נושא',
    'contact.subjectPlaceholder': 'במה נוכל לעזור?',
    'contact.message': 'הודעה',
    'contact.messagePlaceholder': 'פרטו את פנייתכם...',
    'contact.send': 'שלח הודעה',
    'contact.sending': 'שולח...',
    'contact.success': 'ההודעה נשלחה!',
    'contact.hours': 'שעות פעילות',
    'contact.hoursContent': "א'-ה': 09:00-18:00 | ו': 09:00-13:00",
    'contact.consentText': 'אני מסכים/ה לאיסוף ושימוש במידע שלי בהתאם ל',
    'contact.privacyLink': 'מדיניות הפרטיות',
    'contact.consentRequired': 'יש לאשר את מדיניות הפרטיות',
    'contact.error': 'שגיאה בשליחת ההודעה',
    'contact.errorDescription': 'אנא נסה שוב מאוחר יותר',

    // Footer
    'footer.description': 'כלי עבודה מקצועיים ועבודות נגרות בהתאמה אישית במחירים משתלמים. הזמנה קלה ומשלוח לכל הארץ.',
    'footer.rights': 'כל הזכויות שמורות',
    'footer.quickLinks': 'קישורים מהירים',
    'footer.contact': 'יצירת קשר',
    'footer.newsletterTitle': 'הישארו מעודכנים',
    'footer.newsletterSubtitle': 'הירשמו לניוזלטר שלנו וקבלו הנחות בלעדיות',
    'footer.emailPlaceholder': 'האימייל שלך',
    'footer.subscribe': 'הרשמה',
    'footer.privacy': 'מדיניות פרטיות',
    'footer.terms': 'תנאי שימוש',
    'footer.exemptDealer': 'עוסק פטור - המחיר אינו כולל מע"מ',

    // Privacy
    'privacy.title': 'מדיניות פרטיות',
    'privacy.lastUpdated': 'עדכון אחרון',
    'privacy.intro.title': 'מבוא',
    'privacy.intro.content': 'אנו מכבדים את פרטיותך ומחויבים להגן על המידע האישי שלך. מדיניות זו מסבירה כיצד אנו אוספים, משתמשים ומגינים על המידע שלך.',
    'privacy.collection.title': 'מידע שאנו אוספים',
    'privacy.collection.intro': 'אנו עשויים לאסוף את המידע הבא:',
    'privacy.collection.item1': 'פרטים אישיים: שם, כתובת אימייל, מספר טלפון',
    'privacy.collection.item2': 'נתוני גלישה: כתובת IP, סוג דפדפן, דפים שביקרת',
    'privacy.collection.item3': 'מידע על הזמנות: היסטוריית רכישות, העדפות',
    'privacy.collection.item4': 'מידע טכני: עוגיות ונתוני שימוש',
    'privacy.purpose.title': 'מטרת איסוף המידע',
    'privacy.purpose.item1': 'עיבוד הזמנות ומשלוחים',
    'privacy.purpose.item2': 'שיפור חווית המשתמש באתר',
    'privacy.purpose.item3': 'שליחת עדכונים והצעות (בהסכמתך)',
    'privacy.purpose.item4': 'מענה לפניות ושירות לקוחות',
    'privacy.security.title': 'אבטחת מידע',
    'privacy.security.content': 'אנו נוקטים באמצעי אבטחה מתקדמים להגנה על המידע שלך, כולל הצפנה, גישה מוגבלת ואחסון מאובטח. עם זאת, אין שיטת העברה באינטרנט בטוחה ב-100%.',
    'privacy.cookies.title': 'עוגיות (Cookies)',
    'privacy.cookies.content': 'האתר משתמש בעוגיות לשיפור חווית הגלישה, ניתוח תנועה והתאמה אישית. ניתן לשלוט בהגדרות העוגיות דרך הדפדפן שלך.',
    'privacy.thirdParty.title': 'העברת מידע לצדדים שלישיים',
    'privacy.thirdParty.content': 'המידע שלך עשוי להיות מועבר לספקי שירות חיצוניים (כגון חברות משלוחים ועיבוד תשלומים) לצורך ביצוע ההזמנה. חלק מהספקים עשויים להיות מחוץ לישראל.',
    'privacy.rights.title': 'זכויות המשתמש',
    'privacy.rights.intro': 'על פי חוק הגנת הפרטיות, יש לך את הזכויות הבאות:',
    'privacy.rights.item1': 'לעיין במידע שנאסף עליך',
    'privacy.rights.item2': 'לבקש תיקון או מחיקה של המידע',
    'privacy.rights.item3': 'לבטל הסכמה לקבלת דיוור שיווקי',
    'privacy.rights.contact': 'לבקשות בנוגע למידע האישי שלך, פנה אלינו',
    'privacy.contact.title': 'יצירת קשר',
    'privacy.contact.content': 'לשאלות בנוגע למדיניות הפרטיות שלנו, ניתן לפנות אלינו:',

    // Terms
    'terms.title': 'תנאי שימוש',
    'terms.intro.title': 'כללי',
    'terms.intro.content': 'ברוכים הבאים לאתר שלנו. השימוש באתר מהווה הסכמה לתנאים אלה. אנא קראו אותם בעיון.',
    'terms.usage.title': 'כללי שימוש',
    'terms.usage.item1': 'האתר מיועד לשימוש אישי ולא מסחרי בלבד',
    'terms.usage.item2': 'אין להשתמש באתר באופן שעלול לפגוע בפעילותו או במשתמשים אחרים',
    'terms.usage.item3': 'אין להעתיק, לשכפל או להפיץ תוכן מהאתר ללא אישור בכתב',
    'terms.ip.title': 'קניין רוחני',
    'terms.ip.content': 'כל התוכן באתר, לרבות תמונות, טקסטים, לוגו ועיצוב, מוגנים בזכויות יוצרים והם רכושנו הבלעדי.',
    'terms.liability.title': 'הגבלת אחריות',
    'terms.liability.content': 'האתר מסופק "כמות שהוא". איננו אחראים לנזקים עקיפים או תוצאתיים הנובעים מהשימוש באתר או מהמוצרים הנמכרים בו.',
    'terms.purchases.title': 'רכישות ומשלוחים',
    'terms.purchases.content': 'תנאי הרכישה באתר:',
    'terms.purchases.item1': 'המחירים באתר כוללים מע"מ אלא אם צוין אחרת',
    'terms.purchases.item2': 'משלוחים מבוצעים לכל הארץ. זמני האספקה עשויים להשתנות',
    'terms.purchases.item3': 'יש לבדוק את המוצר עם קבלתו ולדווח על פגמים מיד',
    'terms.cancellation.title': 'ביטולים והחזרות',
    'terms.cancellation.content': 'ניתן לבטל הזמנה בהתאם לחוק הגנת הצרכן תוך 14 יום מקבלת המוצר. המוצר יוחזר באריזתו המקורית ובמצב תקין. יתכנו עמלות משלוח וטיפול.',
    'terms.jurisdiction.title': 'סמכות שיפוט',
    'terms.jurisdiction.content': 'הדין החל על תנאים אלה הוא דין מדינת ישראל. כל סכסוך יידון בבתי המשפט המוסמכים בישראל.',
    'terms.contact.title': 'יצירת קשר',
    'terms.contact.content': 'לשאלות בנוגע לתנאי השימוש, ניתן לפנות אלינו:',

    // Cookies consent
    'cookies.title': 'האתר משתמש בעוגיות',
    'cookies.description': 'אנו משתמשים בעוגיות כדי לשפר את חווית הגלישה שלך, לנתח תנועה ולהתאים תוכן.',
    'cookies.learnMore': 'למידע נוסף',
    'cookies.accept': 'אני מסכים/ה',
    'cookies.decline': 'דחייה',

    // About
    'about.title': 'אודות',

    // Auth
    'auth.welcome': 'ברוכים הבאים',
    'auth.description': 'התחברו או הירשמו כדי לגשת לאזור האישי',
    'auth.login': 'התחברות',
    'auth.signup': 'הרשמה',
    'auth.email': 'אימייל',
    'auth.password': 'סיסמה',
    'auth.fullName': 'שם מלא',
    'auth.fullNamePlaceholder': 'הזן את שמך המלא',
    'auth.loginButton': 'התחבר',
    'auth.signupButton': 'הרשם',
    'auth.logout': 'התנתק',
    'auth.loginSuccess': 'התחברת בהצלחה!',
    'auth.signupSuccess': 'נרשמת בהצלחה!',
    'auth.logoutSuccess': 'התנתקת בהצלחה',
    'auth.invalidCredentials': 'אימייל או סיסמה שגויים',
    'auth.alreadyRegistered': 'המשתמש כבר רשום',
    'auth.error': 'שגיאה, נסה שוב',
    'auth.passwordTooShort': 'הסיסמה חייבת להכיל לפחות 6 תווים',
    'auth.passwordHint': 'לפחות 6 תווים',
    'auth.referralCode': 'קוד הפניה (אופציונלי)',
    'auth.referralCodePlaceholder': 'הזן קוד הפניה',
    'auth.referralCodeHint': 'אם קיבלת קוד מחבר, הזן אותו כאן',
    'auth.googleSignIn': 'התחבר עם גוגל',
    'auth.googleSignUp': 'הירשם עם גוגל',
    'auth.orContinueWith': 'או המשך עם',

    // Profile
    'profile.welcome': 'שלום',
    'profile.referralProgram': 'תכנית חבר מביא חבר',
    'profile.referralDescription': 'שתף את הקוד שלך עם חברים וקבל 30₪ הנחה על כל חבר שירכוש ויכתוב ביקורת!',
    'profile.yourCode': 'הקוד שלך',
    'profile.shareLink': 'קישור לשיתוף',
    'profile.copy': 'העתק',
    'profile.linkCopied': 'הקישור הועתק!',
    'profile.codeCopied': 'הקוד הועתק!',
    'profile.howItWorks': 'איך זה עובד?',
    'profile.step1': 'שתף את הקישור או הקוד עם חברים',
    'profile.step2': 'החבר נרשם עם הקוד שלך',
    'profile.step3': 'החבר מבצע רכישה ומזין את הקוד בהזמנה',
    'profile.step4': 'החבר כותב ביקורת 5 כוכבים עם הקוד - ואתה מקבל 30₪!',
    'profile.totalReferrals': 'סה"כ הפניות',
    'profile.pendingReferrals': 'בהמתנה',
    'profile.completedReferrals': 'הושלמו',
    'profile.referralStatus': 'סטטוס הפניות',
    'profile.rewardSent': 'הפרס נשלח!',
    'profile.inProgress': 'בתהליך',
    'profile.purchaseStep': 'רכישה',
    'profile.reviewStep': 'ביקורת',
    'profile.yourCoupon': 'הקופון שלך',

    // Coupon Modal
    'coupon.congratulations': 'מזל טוב!',
    'coupon.earnedMessage': 'החבר שהזמנת סיים את כל הפעולות - הרווחת קופון!',
    'coupon.yourCode': 'קוד הקופון שלך:',
    'coupon.discount': 'הנחה',
    'coupon.useInstructions': 'הזן את הקוד בעת התשלום לקבלת ההנחה',
    'coupon.gotIt': 'מעולה, הבנתי!',
    'profile.earnedCoupons': 'הקופונים שהרווחת',
    'profile.couponFrom': 'קופון מהפניית',

    // Nav
    'nav.profile': 'האזור שלי',
    'nav.login': 'התחברות',

    // Reviews
    'reviews.title': 'ביקורות',
    'reviews.count': 'ביקורות',
    'reviews.writeReview': 'כתוב ביקורת',
    'reviews.rating': 'דירוג',
    'reviews.content': 'תוכן הביקורת',
    'reviews.contentPlaceholder': 'שתף את החוויה שלך עם המוצר...',
    'reviews.referralCode': 'קוד הפניה',
    'reviews.referralCodePlaceholder': 'הזן את קוד ההפניה שלך',
    'reviews.referralHint': 'ביקורת 5 כוכבים עם קוד הפניה תזכה אותך בקרדיט!',
    'reviews.submit': 'שלח ביקורת',
    'reviews.submitted': 'הביקורת נשלחה!',
    'reviews.approvedNow': 'הביקורת אושרה ומוצגת כעת',
    'reviews.pendingApproval': 'הביקורת ממתינה לאישור',
    'reviews.referralCredit': 'קיבלת קרדיט הפניה! 🎉',
    'reviews.submitError': 'שגיאה בשליחת הביקורת',
    'reviews.alreadyReviewed': 'כבר כתבת ביקורת על מוצר זה',
    'reviews.loginToReview': 'התחבר כדי לכתוב ביקורת',
    'reviews.noReviews': 'אין עדיין ביקורות. היה הראשון לכתוב!',
    'reviews.anonymous': 'משתמש אנונימי',

    // Showcase (Featured Product)
    'showcase.badge': 'מוצר נבחר',
    'showcase.title1': 'קמין אש חשמלי',
    'showcase.title2': 'יוקרתי ומודרני',
    'showcase.description': 'חווית אש אותנטית ללא עשן וללא סכנה. שליטה מלאה דרך אפליקציה חכמה, התאמה אישית של צבע הלהבה ועוצמת החום. זמין במגוון גדלים להתאמה מושלמת לכל חלל.',
    'showcase.feature1': 'להבות ריאליסטיות',
    'showcase.feature2': 'חסכוני באנרגיה',
    'showcase.feature3': 'שליטה באפליקציה',
    'showcase.cta': 'לצפייה במוצר',
    'showcase.blueFlame': 'להבה כחולה',
    'showcase.orangeFlame': 'להבה כתומה',
    'showcase.appControl': 'שליטה באפליקציה',

    // Hot Deals Section
    'hotDeals.badge': 'מבצע מיוחד לזמן מוגבל',
    'hotDeals.title': 'מבצעי השבוע',
    'hotDeals.description': 'הנחות בלעדיות על מוצרים נבחרים - עד גמר המלאי',
    'hotDeals.days': 'ימים',
    'hotDeals.hours': 'שעות',
    'hotDeals.minutes': 'דקות',
    'hotDeals.seconds': 'שניות',
    'hotDeals.endsAt': 'המבצע מסתיים ב-20 בינואר 2026',
    'hotDeals.saleBadge': 'מבצע',
    'hotDeals.viewAll': 'לכל המבצעים',

    // Testimonials Section
    'testimonials.badge': 'מה הלקוחות אומרים',
    'testimonials.title': 'לקוחות מרוצים',
    'testimonials.description': 'אלפי לקוחות כבר נהנים מהמוצרים שלנו - הצטרפו גם אתם!',
    'testimonials.verified': 'מאומת',

    // General
    'language': 'שפה',

    // Shipping Countries
    'shipping.availableCountries': 'מדינות משלוח זמינות',
    'shipping.weShipTo': 'אנו שולחים כרגע למדינות אלו בלבד',
    'shipping.israel': 'ישראל',
    'shipping.usa': 'ארה"ב',
    'shipping.japan': 'יפן',
    'shipping.italy': 'איטליה',

    // Trust Section
    'trust.badge': 'תשלום מאובטח',
    'trust.title': 'רכישה בטוחה ומאובטחת',
    'trust.subtitle': 'סליקת כרטיסי האשראי מתבצעת באמצעות PayPlus בשיתוף ישראכרט – המידע שלכם מוצפן ולא נשמר באתר.',
    'trust.pciTitle': 'תקן PCI-DSS',
    'trust.pciDesc': 'תקן האבטחה המחמיר ביותר בעולם הסליקה',
    'trust.sslTitle': 'הצפנת SSL/TLS',
    'trust.sslDesc': 'כל הנתונים מוצפנים ועוברים ישירות לבנק',
    'trust.3dsTitle': '3D Secure',
    'trust.3dsDesc': 'אימות זהות בעל הכרטיס למניעת הונאות',
    'trust.israelcardTitle': 'PayPlus + ישראכרט',
    'trust.israelcardDesc': 'מערכת הסליקה המובילה בישראל',

    // Hero - Updated messaging
    'hero.headlineTagline': 'סטנדרט גבוה. בחירה בטוחה.',
    'hero.whyBuyCta': 'למה לקוחות בוחרים בנו?',

    // Why Buy From Us page
    'whyBuy.pageTitle': 'למה לקנות אצלנו?',
    'whyBuy.pageSubtitle': 'כל מוצר באתר נבחר בקפידה כדי להבטיח איכות, אמינות ושימוש אמיתי — עם שירות ואחריות שמלווים אתכם גם אחרי הקנייה.',
    'whyBuy.qualityTitle': 'בחירה מוקפדת של מוצרים איכותיים',
    'whyBuy.qualityDesc': 'כל מוצר באתר נבחר לפי סטנדרט גבוה של איכות, אמינות ושימוש אמיתי — לא כמות, אלא רמה.',
    'whyBuy.warrantyTitle': 'אחריות ושירות אישי',
    'whyBuy.warrantyDesc': 'אנחנו עומדים מאחורי המוצרים ומעניקים מענה ושירות גם לאחר הקנייה, כדי שתהיו רגועים לאורך כל הדרך.',
    'whyBuy.secureTitle': 'רכישה מאובטחת',
    'whyBuy.secureDesc': 'תהליך הקנייה באתר מוגן ומאובטח, כך שתוכלו להזמין בביטחון מלא.',
    'whyBuy.experienceTitle': 'חוויית קנייה פשוטה וברורה',
    'whyBuy.experienceDesc': 'מידע ברור, ניווט נוח ובחירה קלה — חוויית רכישה נעימה ללא עומס ובלבול.',
    'whyBuy.needHelp': 'צריכים עזרה?',
    'whyBuy.helpDescription': 'לכל שאלה או התייעצות, ניתן לפנות אלינו בטלפון:',
    'whyBuy.happyToServe': 'נשמח לעמוד לשירותכם',
    'whyBuy.startShopping': 'התחילו לקנות',
    'whyBuy.productsShowcase': 'מוצרים נבחרים',
    'whyBuy.trustBadge1': 'איכות מעולה',
    'whyBuy.trustBadge2': 'לקוחות מרוצים',
    'whyBuy.trustBadge3': 'שירות מקצועי',
  },
  en: {
    // Header
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.categories': 'Categories',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'search.placeholder': 'Search products...',
    'search.noResults': 'No results found',

    // Categories
    'categories.title': 'Categories',
    'categories.description': 'Choose a category to view products',
    'categories.viewProducts': 'View Products',
    'categories.screwdrivers': 'Screwdrivers & Bits',
    'categories.screwdriversDesc': 'Manual, electric screwdrivers and professional bit sets',
    'categories.sets': 'Professional Sets',
    'categories.setsDesc': 'Combined tool kits for every task',
    'categories.handtools': 'Hand Tools',
    'categories.handtoolsDesc': 'Hammers, wrenches, pliers and hand tools',
    'categories.accessories': 'Accessories',
    'categories.accessoriesDesc': 'Bags, cases and spare parts',
    'categories.measuring': 'Measuring Tools',
    'categories.measuringDesc': 'Rulers, levels and measuring devices',
    'categories.home': 'Home',
    'categories.homeDesc': 'Useful products for home and family',

    // Hero
    'hero.title': 'Professional Tools',
    'hero.subtitle': 'Quality Without Compromise',
    'hero.cta': 'View All Products',

    'hero.badge': 'Quality products at great prices',
    'hero.headlineLead': 'Custom Carpentry',
    'hero.headlineAccent': 'Made to Order',
    'hero.headlineTail': '',
    'hero.description': 'Tables, dressers, TV units, consoles, stools and more — all solid wood, handcrafted to your exact dimensions.',
    'hero.badge1': 'Solid Wood',
    'hero.badge2': 'Handcrafted',
    'hero.badge3': 'Custom Sizes',
    'hero.primaryCta': 'View Our Work',
    'hero.secondaryCta': 'Contact us',

    'hero.feature1Title': 'Custom Design',
    'hero.feature1Desc': 'Every piece built to your needs',
    'hero.feature2Title': 'Handcrafted',
    'hero.feature2Desc': 'Solid wood with professional finish',

    // Home
    'home.featuredTitle': 'Our Top Products',
    'home.featuredSubtitle': 'Our most popular tools',
    'home.featuredBadge': 'Carefully Selected',
    'home.featuredDescription': 'We selected the most popular products for you - high quality at the best prices in Israel',
    'home.viewAllProducts': 'All Products',
    'home.viewAll': 'View all products',
    'home.viewAllCategories': 'View all categories',
    'home.ctaTitle': 'Ready to get started?',
    'home.ctaDescription': 'Join thousands of happy customers enjoying our quality tools—great prices, wide selection, and nationwide shipping.',
    'home.ctaButton': 'Start shopping',
    'home.contactComfort': 'Your comfort matters to us',
    'home.contactAvailable': 'We are happy to help with any question',
    'home.contactUs': 'Contact us',

    // Carpentry section on Index
    'home.contactCta': 'Contact us',
    'home.portfolioBadge': '✦ Work Portfolio ✦',
    'home.carpentryTitle': 'Carpentry Work',
    'home.carpentryAccent': 'Custom Made',
    'home.carpentryDesc': 'Tables, dressers, TV units, consoles, stools and more — all solid wood, handcrafted.',
    'home.carpentryDescAccent': 'Tell us what you need and we\'ll build it.',
    'home.tag1': '100% Solid Wood',
    'home.tag2': 'Handcrafted',
    'home.tag3': 'Color of your choice',
    'home.tag4': 'Custom sizes',
    'home.labelOutdoorTable': 'Outdoor Table',
    'home.labelTvCabinet': 'TV Cabinet',
    'home.labelVanity': 'Vanity Cabinet',
    'home.labelShelf': 'Shelf',
    'home.labelCoffeeTable': 'Coffee Table',
    'home.labelGlassCabinet': 'Glass Cabinet',
    'home.labelConsole': 'Console',
    'home.beforeAfterTitle': 'Renovation —',
    'home.beforeAfterAccent': 'Before & After',
    'home.tableRenovation': 'Table Renovation',
    'home.deckRenovation': 'Deck Renovation',
    'home.viewAllWorks': 'View all our work',

    // Nav links
    'nav.carpentry': 'Our Carpentry',
    'nav.woodTable': 'Wood Table',

    // Footer
    'footer.ourWorks': 'Our Work',
    'footer.outdoorTable': 'Outdoor Table',
    
    
    // Featured section
    'featured.trending': 'Trending Products',
    'featured.quality': 'Premium Quality',
    'featured.fastDelivery': 'Fast Delivery',
    'featured.spotlight': 'Recommended',
    
    // Car Cover Video Section
    'carCover.badge': 'Our Flagship Product',
    'carCover.videoTitle': 'Automatic Electric Car Cover',
    'carCover.videoDescription': 'Watch how the cover opens and closes automatically to protect your car in any weather',
    'carCover.viewProduct': 'View Full Product',
    'carCover.bestseller': 'Best Seller',
    'carCover.feature1': 'Automatic remote opening',
    'carCover.feature2': 'Sun & rain protection',
    'carCover.feature3': 'Easy & quick installation',
    'carCover.feature4': '12 variations available',
    
    // Products view
    'products.viewProduct': 'View Product',

    // Header
    'header.promoBanner': '✦ We\'re here to help – feel at home ✦',

    // Navigation
    'navigation.back': 'Back',
    'navigation.forward': 'Forward',

    // Referral
    'referral.title': '🎁 Refer a Friend - Get ₪30 Off!',
    'referral.step1': 'Invite a friend to sign up with your code',
    'referral.step2': 'Friend makes a purchase and enters the code',
    'referral.step3': 'Friend writes a 5-star review',
    'referral.reward': '✨ You get a ₪30 coupon directly on the site!',
    'referral.myCode': 'My referral code',
    'product.optionsNotAvailable': 'This option combination is not available',
    'product.selectOtherOptions': 'Please select a different combination',

    // Products
    'products.title': 'Our Products',
    'products.description': 'A wide selection of tools at the best prices in Israel. Easy and convenient shopping with nationwide delivery.',
    'products.addToCart': 'Add to Cart',
    'products.writeReview': 'Write Review',
    'products.viewDetails': 'View Details',
    'products.price': 'Price',
    'products.includesVat': 'Includes VAT',
    'products.outOfStock': 'Out of Stock',
    'products.clearFilter': 'Clear Filter',
    'products.hotDeals': '🔥 Hot Deals 🔥',
    'products.startingFrom': 'From ',
    'products.selectOptions': 'Select Options',
    'products.selectOptionsToast': 'Please select options (color/size) to get accurate pricing',
    'products.savings': 'Save',
    'products.premium': 'Premium',
    'products.reviews': 'Reviews',
    'products.viewOptions': 'View Options',
    'product.allDetails': 'All Details',

    // Product grid
    'productGrid.emptyTitle': 'No products to display',
    'productGrid.emptyDescription': 'No products have been added yet. Tell us what you want to sell and we will add the products for you.',
    'productGrid.loadMore': 'Load more products',
    'productGrid.loading': 'Loading...',
    'productGrid.showingProducts': 'Showing {count} products',
    'productGrid.endOfCatalog': 'You have reached the end of the catalog',
    'productGrid.moreToDiscover': 'More great products to discover',

    // Product detail
    'product.notFound': 'Product not found',
    'product.backToProducts': 'Back to products',
    'product.inStock': 'In stock',
    'product.descriptionTitle': 'Product description',
    'product.options': 'Options',
    'product.quantity': 'Quantity',
    'product.shippingTitle': 'Nationwide delivery',
    'product.shippingSubtitle': 'We ship directly to you',
    'product.fastShipping': 'Nationwide Delivery',
    'product.shippingDays': 'Right to your door',
    'product.securePayment': 'Secure Payment',
    'product.sslEncrypted': 'SSL Encrypted',
    'product.bestPrice': 'Best Price',
    'product.winningPrice': 'Lowest in Israel',
    'product.personalService': 'Personal Service',
    'product.fastSupport': 'Fast Support',

    // Cart
    'cart.title': 'Shopping Cart',
    'cart.empty': 'Your cart is empty',
    'cart.emptyPrompt': 'Add products to your cart to get started',
    'cart.added': 'Added to cart',
    'cart.total': 'Total',
    'cart.checkout': 'Checkout',
    'cart.processing': 'Processing...',
    'cart.items': 'items in cart',
    'cart.referralCode': 'Referral Code',
    'cart.referralCodePlaceholder': 'Enter referral code from a friend',
    'cart.validCode': 'Valid code!',
    'cart.invalidCode': 'Code not found',
    'cart.redirecting': 'Redirecting to checkout...',
    'cart.checkoutError': 'Error during checkout',

    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Have a question? We are here for you and happy to help.',
    'contact.formTitle': 'Send us a message',
    'contact.infoTitle': 'Contact Information',
    'contact.name': 'Full Name',
    'contact.namePlaceholder': 'Your name',
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.subject': 'Subject',
    'contact.subjectPlaceholder': 'How can we help?',
    'contact.message': 'Message',
    'contact.messagePlaceholder': 'Tell us more...',
    'contact.send': 'Send Message',
    'contact.sending': 'Sending...',
    'contact.success': 'Message sent!',
    'contact.hours': 'Business Hours',
    'contact.hoursContent': 'Sun-Thu: 09:00-18:00 | Fri: 09:00-13:00',
    'contact.consentText': 'I agree to the collection and use of my information according to the',
    'contact.privacyLink': 'Privacy Policy',
    'contact.consentRequired': 'You must accept the privacy policy',
    'contact.error': 'Error sending message',
    'contact.errorDescription': 'Please try again later',

    // Footer
    'footer.description': 'A wide selection of tools at great prices. Easy ordering and nationwide shipping.',
    'footer.rights': 'All rights reserved',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact Us',
    'footer.newsletterTitle': 'Stay updated',
    'footer.newsletterSubtitle': 'Subscribe to our newsletter and get exclusive discounts',
    'footer.emailPlaceholder': 'Your email',
    'footer.subscribe': 'Subscribe',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Use',
    'footer.exemptDealer': 'Exempt Dealer - Price does not include VAT',

    // Privacy
    'privacy.title': 'Privacy Policy',
    'privacy.lastUpdated': 'Last updated',
    'privacy.intro.title': 'Introduction',
    'privacy.intro.content': 'We respect your privacy and are committed to protecting your personal information. This policy explains how we collect, use, and protect your information.',
    'privacy.collection.title': 'Information We Collect',
    'privacy.collection.intro': 'We may collect the following information:',
    'privacy.collection.item1': 'Personal details: name, email address, phone number',
    'privacy.collection.item2': 'Browsing data: IP address, browser type, pages visited',
    'privacy.collection.item3': 'Order information: purchase history, preferences',
    'privacy.collection.item4': 'Technical information: cookies and usage data',
    'privacy.purpose.title': 'Purpose of Collection',
    'privacy.purpose.item1': 'Processing orders and deliveries',
    'privacy.purpose.item2': 'Improving user experience on the site',
    'privacy.purpose.item3': 'Sending updates and offers (with your consent)',
    'privacy.purpose.item4': 'Responding to inquiries and customer service',
    'privacy.security.title': 'Data Security',
    'privacy.security.content': 'We employ advanced security measures to protect your information, including encryption, limited access, and secure storage. However, no method of transmission over the Internet is 100% secure.',
    'privacy.cookies.title': 'Cookies',
    'privacy.cookies.content': 'The site uses cookies to improve browsing experience, analyze traffic, and personalization. You can control cookie settings through your browser.',
    'privacy.thirdParty.title': 'Third-Party Data Transfer',
    'privacy.thirdParty.content': 'Your information may be shared with external service providers (such as shipping and payment processing companies) for order fulfillment. Some providers may be located outside of Israel.',
    'privacy.rights.title': 'User Rights',
    'privacy.rights.intro': 'Under privacy law, you have the following rights:',
    'privacy.rights.item1': 'Access information collected about you',
    'privacy.rights.item2': 'Request correction or deletion of information',
    'privacy.rights.item3': 'Opt out of marketing communications',
    'privacy.rights.contact': 'For requests regarding your personal information, contact us at',
    'privacy.contact.title': 'Contact Us',
    'privacy.contact.content': 'For questions about our privacy policy, please contact us:',

    // Terms
    'terms.title': 'Terms of Use',
    'terms.intro.title': 'General',
    'terms.intro.content': 'Welcome to our site. Use of this site constitutes agreement to these terms. Please read them carefully.',
    'terms.usage.title': 'Usage Rules',
    'terms.usage.item1': 'The site is intended for personal, non-commercial use only',
    'terms.usage.item2': 'Do not use the site in a way that could harm its operation or other users',
    'terms.usage.item3': 'Do not copy, duplicate, or distribute site content without written permission',
    'terms.ip.title': 'Intellectual Property',
    'terms.ip.content': 'All site content, including images, text, logos, and design, is protected by copyright and is our exclusive property.',
    'terms.liability.title': 'Limitation of Liability',
    'terms.liability.content': 'The site is provided "as is." We are not liable for indirect or consequential damages resulting from use of the site or products sold on it.',
    'terms.purchases.title': 'Purchases & Shipping',
    'terms.purchases.content': 'Purchase terms:',
    'terms.purchases.item1': 'Prices include VAT unless otherwise stated',
    'terms.purchases.item2': 'Shipping is available nationwide. Delivery times may vary',
    'terms.purchases.item3': 'Check your product upon receipt and report any defects immediately',
    'terms.cancellation.title': 'Cancellations & Returns',
    'terms.cancellation.content': 'Orders can be canceled according to consumer protection law within 14 days of receiving the product. The product must be returned in its original packaging and in good condition. Shipping and handling fees may apply.',
    'terms.jurisdiction.title': 'Jurisdiction',
    'terms.jurisdiction.content': 'These terms are governed by the laws of the State of Israel. Any dispute shall be adjudicated in the competent courts in Israel.',
    'terms.contact.title': 'Contact Us',
    'terms.contact.content': 'For questions about these terms of use, please contact us:',

    // Cookies consent
    'cookies.title': 'This site uses cookies',
    'cookies.description': 'We use cookies to improve your browsing experience, analyze traffic, and personalize content.',
    'cookies.learnMore': 'Learn more',
    'cookies.accept': 'Accept',
    'cookies.decline': 'Decline',

    // About
    'about.title': 'About Us',

    // Auth
    'auth.welcome': 'Welcome',
    'auth.description': 'Log in or sign up to access your personal area',
    'auth.login': 'Login',
    'auth.signup': 'Sign Up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.fullName': 'Full Name',
    'auth.fullNamePlaceholder': 'Enter your full name',
    'auth.loginButton': 'Log In',
    'auth.signupButton': 'Sign Up',
    'auth.logout': 'Log Out',
    'auth.loginSuccess': 'Logged in successfully!',
    'auth.signupSuccess': 'Signed up successfully!',
    'auth.logoutSuccess': 'Logged out successfully',
    'auth.invalidCredentials': 'Invalid email or password',
    'auth.alreadyRegistered': 'User already registered',
    'auth.error': 'Error, please try again',
    'auth.passwordTooShort': 'Password must be at least 6 characters',
    'auth.passwordHint': 'At least 6 characters',
    'auth.referralCode': 'Referral Code (optional)',
    'auth.referralCodePlaceholder': 'Enter referral code',
    'auth.referralCodeHint': 'If you received a code from a friend, enter it here',
    'auth.googleSignIn': 'Sign in with Google',
    'auth.googleSignUp': 'Sign up with Google',
    'auth.orContinueWith': 'or continue with',

    // Profile
    'profile.welcome': 'Hello',
    'profile.referralProgram': 'Refer a Friend Program',
    'profile.referralDescription': 'Share your code with friends and get ₪30 discount for each friend who purchases and writes a review!',
    'profile.yourCode': 'Your Code',
    'profile.shareLink': 'Share Link',
    'profile.copy': 'Copy',
    'profile.linkCopied': 'Link copied!',
    'profile.codeCopied': 'Code copied!',
    'profile.howItWorks': 'How does it work?',
    'profile.step1': 'Share your link or code with friends',
    'profile.step2': 'Your friend signs up with your code',
    'profile.step3': 'Your friend makes a purchase and enters the code',
    'profile.step4': 'Your friend writes a 5-star review with the code - and you get ₪30!',
    'profile.totalReferrals': 'Total Referrals',
    'profile.pendingReferrals': 'Pending',
    'profile.completedReferrals': 'Completed',
    'profile.referralStatus': 'Referral Status',
    'profile.rewardSent': 'Reward Sent!',
    'profile.inProgress': 'In Progress',
    'profile.purchaseStep': 'Purchase',
    'profile.reviewStep': 'Review',
    'profile.yourCoupon': 'Your Coupon',

    // Coupon Modal
    'coupon.congratulations': 'Congratulations!',
    'coupon.earnedMessage': 'Your friend completed all the steps - you earned a coupon!',
    'coupon.yourCode': 'Your coupon code:',
    'coupon.discount': 'discount',
    'coupon.useInstructions': 'Enter this code at checkout to get your discount',
    'coupon.gotIt': 'Got it!',
    'profile.earnedCoupons': 'Your Earned Coupons',
    'profile.couponFrom': 'Coupon from',

    // Nav
    'nav.profile': 'My Account',
    'nav.login': 'Login',

    // Reviews
    'reviews.title': 'Reviews',
    'reviews.count': 'reviews',
    'reviews.writeReview': 'Write a Review',
    'reviews.rating': 'Rating',
    'reviews.content': 'Review Content',
    'reviews.contentPlaceholder': 'Share your experience with this product...',
    'reviews.referralCode': 'Referral Code',
    'reviews.referralCodePlaceholder': 'Enter your referral code',
    'reviews.referralHint': 'A 5-star review with referral code earns you credit!',
    'reviews.submit': 'Submit Review',
    'reviews.submitted': 'Review submitted!',
    'reviews.approvedNow': 'Your review is now live',
    'reviews.pendingApproval': 'Your review is pending approval',
    'reviews.referralCredit': 'You earned referral credit! 🎉',
    'reviews.submitError': 'Error submitting review',
    'reviews.alreadyReviewed': 'You have already reviewed this product',
    'reviews.loginToReview': 'Log in to write a review',
    'reviews.noReviews': 'No reviews yet. Be the first to write one!',
    'reviews.anonymous': 'Anonymous User',

    // Showcase (Featured Product)
    'showcase.badge': 'Featured Product',
    'showcase.title1': 'Electric Fireplace',
    'showcase.title2': 'Luxurious & Modern',
    'showcase.description': 'Authentic fire experience without smoke or danger. Full control via smart app, customize flame color and heat intensity. Available in various sizes for perfect fit in any space.',
    'showcase.feature1': 'Realistic Flames',
    'showcase.feature2': 'Energy Efficient',
    'showcase.feature3': 'App Control',
    'showcase.cta': 'View Product',
    'showcase.blueFlame': 'Blue Flame',
    'showcase.orangeFlame': 'Orange Flame',
    'showcase.appControl': 'App Control',

    // Hot Deals Section
    'hotDeals.badge': 'Limited Time Offer',
    'hotDeals.title': 'Weekly Deals',
    'hotDeals.description': 'Exclusive discounts on selected products - while stocks last',
    'hotDeals.days': 'Days',
    'hotDeals.hours': 'Hours',
    'hotDeals.minutes': 'Minutes',
    'hotDeals.seconds': 'Seconds',
    'hotDeals.endsAt': 'Sale ends on January 20, 2026',
    'hotDeals.saleBadge': 'Sale',
    'hotDeals.viewAll': 'View All Deals',

    // Testimonials Section
    'testimonials.badge': 'What Our Customers Say',
    'testimonials.title': 'Happy Customers',
    'testimonials.description': 'Thousands of customers already enjoy our products - join them!',
    'testimonials.verified': 'Verified',

    // General
    'language': 'Language',

    // Shipping Countries
    'shipping.availableCountries': 'Available Shipping Countries',
    'shipping.weShipTo': 'We currently ship to these countries only',
    'shipping.israel': 'Israel',
    'shipping.usa': 'USA',
    'shipping.japan': 'Japan',
    'shipping.italy': 'Italy',

    // Trust Section
    'trust.badge': 'Secure Payment',
    'trust.title': 'Safe & Secure Shopping',
    'trust.subtitle': 'Credit card processing via PayPlus & Isracard – your data is encrypted and never stored on our site.',
    'trust.pciTitle': 'PCI-DSS Compliant',
    'trust.pciDesc': 'Highest security standard in payment processing',
    'trust.sslTitle': 'SSL/TLS Encryption',
    'trust.sslDesc': 'All data encrypted and sent directly to bank',
    'trust.3dsTitle': '3D Secure',
    'trust.3dsDesc': 'Cardholder verification to prevent fraud',
    'trust.israelcardTitle': 'PayPlus + Isracard',
    'trust.israelcardDesc': 'Leading payment system in Israel',

    // Hero - Updated messaging
    'hero.headlineTagline': 'High standards. Safe choice.',
    'hero.whyBuyCta': 'Why customers choose us?',

    // Why Buy From Us page
    'whyBuy.pageTitle': 'Why Buy From Us?',
    'whyBuy.pageSubtitle': 'Every product on our site is carefully selected to ensure quality, reliability, and real-world usefulness — with service and warranty that support you even after your purchase.',
    'whyBuy.qualityTitle': 'Carefully Selected Quality Products',
    'whyBuy.qualityDesc': 'Every product is chosen based on high standards of quality, reliability, and real-world use — not quantity, but quality.',
    'whyBuy.warrantyTitle': 'Warranty & Personal Service',
    'whyBuy.warrantyDesc': 'We stand behind our products and provide support even after your purchase, so you can feel confident every step of the way.',
    'whyBuy.secureTitle': 'Secure Purchase',
    'whyBuy.secureDesc': 'The checkout process is protected and secure, so you can order with complete confidence.',
    'whyBuy.experienceTitle': 'Simple & Clear Shopping Experience',
    'whyBuy.experienceDesc': 'Clear information, easy navigation, and simple choices — a pleasant shopping experience without clutter or confusion.',
    'whyBuy.needHelp': 'Need Help?',
    'whyBuy.helpDescription': 'For any question or consultation, you can reach us by phone:',
    'whyBuy.happyToServe': 'We\'re happy to assist you',
    'whyBuy.startShopping': 'Start Shopping',
    'whyBuy.productsShowcase': 'Featured Products',
    'whyBuy.trustBadge1': 'Premium Quality',
    'whyBuy.trustBadge2': 'Happy Customers',
    'whyBuy.trustBadge3': 'Professional Service',
  },
  ar: {
    // Header
    'nav.home': 'الرئيسية',
    'nav.products': 'المنتجات',
    'nav.categories': 'الفئات',
    'nav.about': 'حولنا',
    'nav.contact': 'اتصل بنا',
    'search.placeholder': 'البحث عن المنتجات...',
    'search.noResults': 'لم يتم العثور على نتائج',

    // Categories
    'categories.title': 'الفئات',
    'categories.description': 'اختر فئة لعرض المنتجات',
    'categories.viewProducts': 'عرض المنتجات',
    'categories.screwdrivers': 'مفكات ورؤوس',
    'categories.screwdriversDesc': 'مفكات يدوية وكهربائية ومجموعات رؤوس احترافية',
    'categories.sets': 'مجموعات احترافية',
    'categories.setsDesc': 'مجموعات أدوات متكاملة لكل مهمة',
    'categories.handtools': 'أدوات يدوية',
    'categories.handtoolsDesc': 'مطارق ومفاتيح وكماشات وأدوات يدوية',
    'categories.accessories': 'إكسسوارات',
    'categories.accessoriesDesc': 'حقائب وصناديق وقطع غيار',
    'categories.measuring': 'أدوات القياس',
    'categories.measuringDesc': 'مساطر ومستويات وأجهزة قياس',
    'categories.home': 'المنزل',
    'categories.homeDesc': 'منتجات مفيدة للمنزل والعائلة',

    // Hero
    'hero.title': 'أدوات احترافية',
    'hero.subtitle': 'جودة بدون تنازلات',
    'hero.cta': 'عرض جميع المنتجات',

    'hero.badge': 'منتجات عالية الجودة بأسعار رائعة',
    'hero.headlineLead': 'كل ما تحتاجه',
    'hero.headlineAccent': '– في مكان واحد',
    'hero.headlineTail': '',
    'hero.description': 'أدوات احترافية وأعمال نجارة مخصصة بأفضل الأسعار.',
    'hero.primaryCta': 'تصفح المنتجات',
    'hero.secondaryCta': 'اتصل بنا',

    'hero.feature1Title': 'أدوات',
    'hero.feature1Desc': 'مفكات، طقوم، أدوات قياس والمزيد',
    'hero.feature2Title': 'أعمال نجارة',
    'hero.feature2Desc': 'طاولات وخزائن وأثاث حسب الطلب',

    // Home
    'home.featuredTitle': 'منتجات مميزة',
    'home.featuredSubtitle': 'أكثر أدواتنا شيوعًا',
    'home.viewAll': 'عرض جميع المنتجات',
    'home.viewAllCategories': 'عرض جميع الفئات',
    'home.ctaTitle': 'هل أنت مستعد للبدء؟',
    'home.ctaDescription': 'انضم إلى آلاف العملاء السعداء الذين يستمتعون بأدواتنا عالية الجودة — أسعار رائعة وتشكيلة واسعة وشحن لجميع المناطق.',
    'home.ctaButton': 'ابدأ التسوق',
    'home.contactComfort': 'راحتكم تهمنا',
    'home.contactAvailable': 'نحن سعداء بالإجابة على أي سؤال',
    'home.contactUs': 'تواصلوا معنا',

    // Car Cover Video Section
    'carCover.badge': 'منتجنا الرئيسي',
    'carCover.videoTitle': 'غطاء سيارة كهربائي أوتوماتيكي',
    'carCover.videoDescription': 'شاهد كيف يفتح ويغلق الغطاء تلقائيًا لحماية سيارتك في أي طقس',
    'carCover.viewProduct': 'عرض المنتج الكامل',
    'carCover.bestseller': 'الأكثر مبيعًا',
    'carCover.feature1': 'فتح أوتوماتيكي بالريموت',
    'carCover.feature2': 'حماية من الشمس والمطر',
    'carCover.feature3': 'تركيب سهل وسريع',
    'carCover.feature4': '12 تنويعات متاحة',

    // Header
    'header.promoBanner': '✦ أفضل الأسعار في البلاد ✦',

    // Navigation
    'navigation.back': 'رجوع',
    'navigation.forward': 'تقدم',

    // Referral
    'referral.title': '🎁 أحضر صديقًا - احصل على خصم 30₪!',
    'referral.step1': 'ادعُ صديقًا للتسجيل باستخدام رمزك',
    'referral.step2': 'يقوم الصديق بالشراء ويدخل الرمز',
    'referral.step3': 'يكتب الصديق مراجعة 5 نجوم',
    'referral.reward': '✨ تحصل على قسيمة 30₪ مباشرة على الموقع!',
    'referral.myCode': 'رمز الإحالة الخاص بي',
    'product.optionsNotAvailable': 'مجموعة الخيارات هذه غير متاحة',
    'product.selectOtherOptions': 'يرجى اختيار مجموعة أخرى',

    // Products
    'products.title': 'منتجاتنا',
    'products.description': 'تشكيلة واسعة من الأدوات بأفضل الأسعار في البلاد. تسوق مريح وسهل مع توصيل لجميع المناطق.',
    'products.addToCart': 'أضف للسلة',
    'products.writeReview': 'اكتب مراجعة',
    'products.viewDetails': 'عرض التفاصيل',
    'products.price': 'السعر',
    'products.includesVat': 'شامل ضريبة القيمة المضافة',
    'products.outOfStock': 'نفذ من المخزون',
    'products.clearFilter': 'مسح الفلتر',
    'products.hotDeals': '🔥 عروض ساخنة 🔥',
    'products.startingFrom': 'من ',
    'products.selectOptions': 'اختر الخيارات',
    'products.selectOptionsToast': 'يرجى اختيار الخيارات (اللون/المقاس) للحصول على السعر الدقيق',
    'products.savings': 'وفر',
    'products.premium': 'ممتاز',
    'products.reviews': 'التقييمات',
    'products.viewOptions': 'عرض الخيارات',
    'product.allDetails': 'كل التفاصيل',

    // Product grid
    'productGrid.emptyTitle': 'لا توجد منتجات للعرض',
    'productGrid.emptyDescription': 'لم تتم إضافة منتجات بعد. أخبرنا بما تريد بيعه وسنضيف المنتجات لك.',
    'productGrid.loadMore': 'تحميل المزيد',
    'productGrid.loading': 'جارٍ التحميل...',
    'productGrid.showingProducts': 'عرض {count} منتج',
    'productGrid.endOfCatalog': 'وصلت إلى نهاية الكتالوج',
    'productGrid.moreToDiscover': 'المزيد من المنتجات الرائعة للاكتشاف',

    // Product detail
    'product.notFound': 'المنتج غير موجود',
    'product.backToProducts': 'العودة إلى المنتجات',
    'product.inStock': 'متوفر',
    'product.descriptionTitle': 'وصف المنتج',
    'product.options': 'الخيارات',
    'product.quantity': 'الكمية',
    'product.shippingTitle': 'توصيل لجميع المناطق',
    'product.shippingSubtitle': 'نقوم بالشحن مباشرة إليك',
    'product.fastShipping': 'توصيل لجميع المناطق',
    'product.shippingDays': 'إلى باب منزلك',
    'product.securePayment': 'دفع آمن',
    'product.sslEncrypted': 'تشفير SSL',
    'product.bestPrice': 'أفضل سعر',
    'product.winningPrice': 'الأرخص في إسرائيل',
    'product.personalService': 'خدمة شخصية',
    'product.fastSupport': 'دعم سريع',

    // Cart
    'cart.title': 'سلة التسوق',
    'cart.empty': 'سلتك فارغة',
    'cart.emptyPrompt': 'أضف منتجات إلى سلتك للبدء',
    'cart.added': 'تمت الإضافة إلى السلة',
    'cart.total': 'المجموع',
    'cart.checkout': 'إتمام الشراء',
    'cart.processing': 'جارٍ المعالجة...',
    'cart.items': 'عناصر في السلة',
    'cart.referralCode': 'رمز الإحالة',
    'cart.referralCodePlaceholder': 'أدخل رمز الإحالة من صديق',
    'cart.validCode': 'رمز صالح!',
    'cart.invalidCode': 'الرمز غير موجود',
    'cart.redirecting': 'جارٍ التحويل إلى الدفع...',
    'cart.checkoutError': 'خطأ أثناء الدفع',

    // Contact
    'contact.title': 'اتصل بنا',
    'contact.subtitle': 'هل لديك سؤال؟ نحن هنا لمساعدتك.',
    'contact.formTitle': 'أرسل لنا رسالة',
    'contact.infoTitle': 'معلومات الاتصال',
    'contact.name': 'الاسم الكامل',
    'contact.namePlaceholder': 'اسمك',
    'contact.email': 'البريد الإلكتروني',
    'contact.phone': 'الهاتف',
    'contact.subject': 'الموضوع',
    'contact.subjectPlaceholder': 'كيف يمكننا مساعدتك؟',
    'contact.message': 'الرسالة',
    'contact.messagePlaceholder': 'أخبرنا المزيد...',
    'contact.send': 'إرسال الرسالة',
    'contact.sending': 'جاري الإرسال...',
    'contact.success': 'تم إرسال الرسالة!',
    'contact.hours': 'ساعات العمل',
    'contact.hoursContent': 'أحد-خميس: 09:00-18:00 | جمعة: 09:00-13:00',
    'contact.consentText': 'أوافق على جمع واستخدام معلوماتي وفقًا لـ',
    'contact.privacyLink': 'سياسة الخصوصية',
    'contact.consentRequired': 'يجب الموافقة على سياسة الخصوصية',
    'contact.error': 'خطأ في إرسال الرسالة',
    'contact.errorDescription': 'يرجى المحاولة مرة أخرى لاحقًا',

    // Footer
    'footer.description': 'تشكيلة واسعة من الأدوات بأسعار رائعة. طلب سهل وشحن لجميع المناطق.',
    'footer.rights': 'جميع الحقوق محفوظة',
    'footer.quickLinks': 'روابط سريعة',
    'footer.contact': 'اتصل بنا',
    'footer.newsletterTitle': 'ابقَ على اطلاع',
    'footer.newsletterSubtitle': 'اشترك في نشرتنا البريدية واحصل على خصومات حصرية',
    'footer.emailPlaceholder': 'بريدك الإلكتروني',
    'footer.subscribe': 'اشترك',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.terms': 'شروط الاستخدام',
    'footer.exemptDealer': 'تاجر معفى - السعر لا يشمل ضريبة القيمة المضافة',

    // Privacy
    'privacy.title': 'سياسة الخصوصية',
    'privacy.lastUpdated': 'آخر تحديث',
    'privacy.intro.title': 'مقدمة',
    'privacy.intro.content': 'نحن نحترم خصوصيتك ونلتزم بحماية معلوماتك الشخصية. توضح هذه السياسة كيفية جمع واستخدام وحماية معلوماتك.',
    'privacy.collection.title': 'المعلومات التي نجمعها',
    'privacy.collection.intro': 'قد نجمع المعلومات التالية:',
    'privacy.collection.item1': 'التفاصيل الشخصية: الاسم، البريد الإلكتروني، رقم الهاتف',
    'privacy.collection.item2': 'بيانات التصفح: عنوان IP، نوع المتصفح، الصفحات التي تمت زيارتها',
    'privacy.collection.item3': 'معلومات الطلب: سجل الشراء، التفضيلات',
    'privacy.collection.item4': 'المعلومات التقنية: ملفات تعريف الارتباط وبيانات الاستخدام',
    'privacy.purpose.title': 'الغرض من الجمع',
    'privacy.purpose.item1': 'معالجة الطلبات والتوصيل',
    'privacy.purpose.item2': 'تحسين تجربة المستخدم على الموقع',
    'privacy.purpose.item3': 'إرسال التحديثات والعروض (بموافقتك)',
    'privacy.purpose.item4': 'الرد على الاستفسارات وخدمة العملاء',
    'privacy.security.title': 'أمان البيانات',
    'privacy.security.content': 'نستخدم إجراءات أمان متقدمة لحماية معلوماتك، بما في ذلك التشفير والوصول المحدود والتخزين الآمن. ومع ذلك، لا توجد طريقة نقل عبر الإنترنت آمنة بنسبة 100%.',
    'privacy.cookies.title': 'ملفات تعريف الارتباط',
    'privacy.cookies.content': 'يستخدم الموقع ملفات تعريف الارتباط لتحسين تجربة التصفح وتحليل حركة المرور والتخصيص. يمكنك التحكم في إعدادات ملفات تعريف الارتباط من خلال متصفحك.',
    'privacy.thirdParty.title': 'نقل البيانات لأطراف ثالثة',
    'privacy.thirdParty.content': 'قد تتم مشاركة معلوماتك مع مزودي خدمات خارجيين (مثل شركات الشحن ومعالجة المدفوعات) لتنفيذ الطلب. قد يكون بعض المزودين خارج إسرائيل.',
    'privacy.rights.title': 'حقوق المستخدم',
    'privacy.rights.intro': 'بموجب قانون الخصوصية، لديك الحقوق التالية:',
    'privacy.rights.item1': 'الوصول إلى المعلومات المجمعة عنك',
    'privacy.rights.item2': 'طلب تصحيح أو حذف المعلومات',
    'privacy.rights.item3': 'إلغاء الاشتراك في الاتصالات التسويقية',
    'privacy.rights.contact': 'لطلبات بشأن معلوماتك الشخصية، تواصل معنا على',
    'privacy.contact.title': 'اتصل بنا',
    'privacy.contact.content': 'للأسئلة حول سياسة الخصوصية، يرجى التواصل معنا:',

    // Terms
    'terms.title': 'شروط الاستخدام',
    'terms.intro.title': 'عام',
    'terms.intro.content': 'مرحبًا بك في موقعنا. استخدام هذا الموقع يشكل موافقة على هذه الشروط. يرجى قراءتها بعناية.',
    'terms.usage.title': 'قواعد الاستخدام',
    'terms.usage.item1': 'الموقع مخصص للاستخدام الشخصي وغير التجاري فقط',
    'terms.usage.item2': 'لا تستخدم الموقع بطريقة قد تضر بتشغيله أو المستخدمين الآخرين',
    'terms.usage.item3': 'لا تنسخ أو تكرر أو توزع محتوى الموقع دون إذن كتابي',
    'terms.ip.title': 'الملكية الفكرية',
    'terms.ip.content': 'جميع محتويات الموقع، بما في ذلك الصور والنصوص والشعارات والتصميم، محمية بحقوق النشر وهي ملكيتنا الحصرية.',
    'terms.liability.title': 'تحديد المسؤولية',
    'terms.liability.content': 'يتم تقديم الموقع "كما هو". لسنا مسؤولين عن الأضرار غير المباشرة أو التبعية الناتجة عن استخدام الموقع أو المنتجات المباعة فيه.',
    'terms.purchases.title': 'المشتريات والشحن',
    'terms.purchases.content': 'شروط الشراء:',
    'terms.purchases.item1': 'الأسعار تشمل ضريبة القيمة المضافة ما لم يُذكر خلاف ذلك',
    'terms.purchases.item2': 'الشحن متاح لجميع أنحاء البلاد. قد تختلف أوقات التسليم',
    'terms.purchases.item3': 'تحقق من منتجك عند الاستلام وأبلغ عن أي عيوب فورًا',
    'terms.cancellation.title': 'الإلغاءات والإرجاع',
    'terms.cancellation.content': 'يمكن إلغاء الطلبات وفقًا لقانون حماية المستهلك خلال 14 يومًا من استلام المنتج. يجب إعادة المنتج في عبوته الأصلية وبحالة جيدة. قد تُطبق رسوم الشحن والمناولة.',
    'terms.jurisdiction.title': 'الاختصاص القضائي',
    'terms.jurisdiction.content': 'تخضع هذه الشروط لقوانين دولة إسرائيل. أي نزاع يُحكم فيه في المحاكم المختصة في إسرائيل.',
    'terms.contact.title': 'اتصل بنا',
    'terms.contact.content': 'للأسئلة حول شروط الاستخدام هذه، يرجى التواصل معنا:',

    // Cookies consent
    'cookies.title': 'هذا الموقع يستخدم ملفات تعريف الارتباط',
    'cookies.description': 'نستخدم ملفات تعريف الارتباط لتحسين تجربة التصفح وتحليل حركة المرور وتخصيص المحتوى.',
    'cookies.learnMore': 'اعرف المزيد',
    'cookies.accept': 'موافق',
    'cookies.decline': 'رفض',

    // About
    'about.title': 'حولنا',

    // Auth
    'auth.welcome': 'مرحباً',
    'auth.description': 'سجّل دخولك أو أنشئ حساباً للوصول إلى منطقتك الشخصية',
    'auth.login': 'تسجيل الدخول',
    'auth.signup': 'إنشاء حساب',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.fullName': 'الاسم الكامل',
    'auth.fullNamePlaceholder': 'أدخل اسمك الكامل',
    'auth.loginButton': 'تسجيل الدخول',
    'auth.signupButton': 'إنشاء حساب',
    'auth.logout': 'تسجيل الخروج',
    'auth.loginSuccess': 'تم تسجيل الدخول بنجاح!',
    'auth.signupSuccess': 'تم إنشاء الحساب بنجاح!',
    'auth.logoutSuccess': 'تم تسجيل الخروج بنجاح',
    'auth.invalidCredentials': 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
    'auth.alreadyRegistered': 'المستخدم مسجل بالفعل',
    'auth.error': 'خطأ، يرجى المحاولة مرة أخرى',
    'auth.passwordTooShort': 'يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل',
    'auth.passwordHint': '6 أحرف على الأقل',
    'auth.referralCode': 'رمز الإحالة (اختياري)',
    'auth.referralCodePlaceholder': 'أدخل رمز الإحالة',
    'auth.referralCodeHint': 'إذا حصلت على رمز من صديق، أدخله هنا',
    'auth.googleSignIn': 'تسجيل الدخول بجوجل',
    'auth.googleSignUp': 'إنشاء حساب بجوجل',
    'auth.orContinueWith': 'أو تابع باستخدام',

    // Profile
    'profile.welcome': 'مرحباً',
    'profile.referralProgram': 'برنامج إحالة الأصدقاء',
    'profile.referralDescription': 'شارك رمزك مع الأصدقاء واحصل على خصم 30₪ لكل صديق يشتري ويكتب تقييم!',
    'profile.yourCode': 'رمزك',
    'profile.shareLink': 'رابط المشاركة',
    'profile.copy': 'نسخ',
    'profile.linkCopied': 'تم نسخ الرابط!',
    'profile.codeCopied': 'تم نسخ الرمز!',
    'profile.howItWorks': 'كيف يعمل؟',
    'profile.step1': 'شارك رابطك أو رمزك مع الأصدقاء',
    'profile.step2': 'يسجل صديقك باستخدام رمزك',
    'profile.step3': 'صديقك يشتري ويدخل الرمز',
    'profile.step4': 'صديقك يكتب تقييم 5 نجوم مع الرمز - وتحصل على 30₪!',
    'profile.totalReferrals': 'إجمالي الإحالات',
    'profile.pendingReferrals': 'قيد الانتظار',
    'profile.completedReferrals': 'مكتملة',
    'profile.referralStatus': 'حالة الإحالات',
    'profile.rewardSent': 'تم إرسال المكافأة!',
    'profile.inProgress': 'قيد التنفيذ',
    'profile.purchaseStep': 'الشراء',
    'profile.reviewStep': 'التقييم',
    'profile.yourCoupon': 'قسيمتك',

    // Coupon Modal
    'coupon.congratulations': 'تهانينا!',
    'coupon.earnedMessage': 'أكمل صديقك جميع الخطوات - لقد ربحت قسيمة!',
    'coupon.yourCode': 'رمز القسيمة الخاص بك:',
    'coupon.discount': 'خصم',
    'coupon.useInstructions': 'أدخل هذا الرمز عند الدفع للحصول على الخصم',
    'coupon.gotIt': 'فهمت!',
    'profile.earnedCoupons': 'القسائم التي ربحتها',
    'profile.couponFrom': 'قسيمة من',

    // Nav
    'nav.profile': 'حسابي',
    'nav.login': 'تسجيل الدخول',

    // Reviews
    'reviews.title': 'التقييمات',
    'reviews.count': 'تقييمات',
    'reviews.writeReview': 'اكتب تقييم',
    'reviews.rating': 'التقييم',
    'reviews.content': 'محتوى التقييم',
    'reviews.contentPlaceholder': 'شارك تجربتك مع هذا المنتج...',
    'reviews.referralCode': 'رمز الإحالة',
    'reviews.referralCodePlaceholder': 'أدخل رمز الإحالة الخاص بك',
    'reviews.referralHint': 'تقييم 5 نجوم مع رمز الإحالة يكسبك رصيد!',
    'reviews.submit': 'إرسال التقييم',
    'reviews.submitted': 'تم إرسال التقييم!',
    'reviews.approvedNow': 'تقييمك مباشر الآن',
    'reviews.pendingApproval': 'تقييمك قيد المراجعة',
    'reviews.referralCredit': 'حصلت على رصيد إحالة! 🎉',
    'reviews.submitError': 'خطأ في إرسال التقييم',
    'reviews.alreadyReviewed': 'لقد قيمت هذا المنتج بالفعل',
    'reviews.loginToReview': 'سجل دخول لكتابة تقييم',
    'reviews.noReviews': 'لا توجد تقييمات بعد. كن أول من يكتب!',
    'reviews.anonymous': 'مستخدم مجهول',

    // Showcase (Featured Product)
    'showcase.badge': 'منتج مميز',
    'showcase.title1': 'مدفأة كهربائية',
    'showcase.title2': 'فاخرة وعصرية',
    'showcase.description': 'تجربة نار حقيقية بدون دخان أو خطر. تحكم كامل عبر تطبيق ذكي، تخصيص لون اللهب وشدة الحرارة. متوفر بأحجام متعددة للتناسب المثالي مع أي مساحة.',
    'showcase.feature1': 'ألسنة لهب واقعية',
    'showcase.feature2': 'موفر للطاقة',
    'showcase.feature3': 'تحكم بالتطبيق',
    'showcase.cta': 'عرض المنتج',
    'showcase.blueFlame': 'لهب أزرق',
    'showcase.orangeFlame': 'لهب برتقالي',
    'showcase.appControl': 'تحكم بالتطبيق',

    // Hot Deals Section
    'hotDeals.badge': 'عرض لفترة محدودة',
    'hotDeals.title': 'عروض الأسبوع',
    'hotDeals.description': 'خصومات حصرية على منتجات مختارة - حتى نفاد الكمية',
    'hotDeals.days': 'أيام',
    'hotDeals.hours': 'ساعات',
    'hotDeals.minutes': 'دقائق',
    'hotDeals.seconds': 'ثواني',
    'hotDeals.endsAt': 'العرض ينتهي في 20 يناير 2026',
    'hotDeals.saleBadge': 'عرض',
    'hotDeals.viewAll': 'عرض جميع العروض',

    // General
    'language': 'اللغة',

    // Trust Section
    'trust.badge': 'دفع آمن',
    'trust.title': 'تسوق آمن ومضمون',
    'trust.subtitle': 'معالجة بطاقات الائتمان عبر PayPlus وישראכרט – بياناتك مشفرة ولا تُخزن على موقعنا.',
    'trust.pciTitle': 'معيار PCI-DSS',
    'trust.pciDesc': 'أعلى معيار أمان في معالجة المدفوعات',
    'trust.sslTitle': 'تشفير SSL/TLS',
    'trust.sslDesc': 'جميع البيانات مشفرة وترسل مباشرة للبنك',
    'trust.3dsTitle': '3D Secure',
    'trust.3dsDesc': 'التحقق من هوية حامل البطاقة لمنع الاحتيال',
    'trust.israelcardTitle': 'PayPlus + Isracard',
    'trust.israelcardDesc': 'نظام الدفع الرائد في إسرائيل',

    // Hero - Updated messaging
    'hero.headlineTagline': 'معايير عالية. اختيار آمن.',
    'hero.whyBuyCta': 'لماذا يختارنا العملاء؟',

    // Why Buy From Us page
    'whyBuy.pageTitle': 'لماذا تشتري منا؟',
    'whyBuy.pageSubtitle': 'كل منتج في موقعنا مختار بعناية لضمان الجودة والموثوقية والاستخدام الفعلي — مع خدمة وضمان يرافقانك حتى بعد الشراء.',
    'whyBuy.qualityTitle': 'منتجات عالية الجودة مختارة بعناية',
    'whyBuy.qualityDesc': 'كل منتج يتم اختياره وفق معايير عالية من الجودة والموثوقية والاستخدام الحقيقي — ليس الكمية بل الجودة.',
    'whyBuy.warrantyTitle': 'ضمان وخدمة شخصية',
    'whyBuy.warrantyDesc': 'نحن نقف وراء منتجاتنا ونقدم الدعم حتى بعد الشراء، لتشعر بالثقة في كل خطوة.',
    'whyBuy.secureTitle': 'شراء آمن',
    'whyBuy.secureDesc': 'عملية الشراء محمية وآمنة، حتى تتمكن من الطلب بثقة تامة.',
    'whyBuy.experienceTitle': 'تجربة تسوق بسيطة وواضحة',
    'whyBuy.experienceDesc': 'معلومات واضحة، تصفح سهل واختيار بسيط — تجربة تسوق ممتعة بدون فوضى أو ارتباك.',
    'whyBuy.needHelp': 'تحتاج مساعدة؟',
    'whyBuy.helpDescription': 'لأي سؤال أو استشارة، يمكنك الاتصال بنا على:',
    'whyBuy.happyToServe': 'سعداء بخدمتكم',
    'whyBuy.startShopping': 'ابدأ التسوق',
    'whyBuy.productsShowcase': 'منتجات مميزة',
    'whyBuy.trustBadge1': 'جودة ممتازة',
    'whyBuy.trustBadge2': 'عملاء راضون',
    'whyBuy.trustBadge3': 'خدمة احترافية',
  },
  ru: {
    // Header
    'nav.home': 'Главная',
    'nav.products': 'Товары',
    'nav.categories': 'Категории',
    'nav.about': 'О нас',
    'nav.contact': 'Контакты',
    'search.placeholder': 'Поиск товаров...',
    'search.noResults': 'Ничего не найдено',

    // Categories
    'categories.title': 'Категории',
    'categories.description': 'Выберите категорию для просмотра товаров',
    'categories.viewProducts': 'Смотреть товары',
    'categories.screwdrivers': 'Отвертки и биты',
    'categories.screwdriversDesc': 'Ручные, электрические отвертки и профессиональные наборы бит',
    'categories.sets': 'Профессиональные наборы',
    'categories.setsDesc': 'Комплекты инструментов для любой задачи',
    'categories.handtools': 'Ручной инструмент',
    'categories.handtoolsDesc': 'Молотки, ключи, плоскогубцы и ручные инструменты',
    'categories.accessories': 'Аксессуары',
    'categories.accessoriesDesc': 'Сумки, кейсы и запчасти',
    'categories.measuring': 'Измерительный инструмент',
    'categories.measuringDesc': 'Линейки, уровни и измерительные приборы',
    'categories.home': 'Дом',
    'categories.homeDesc': 'Полезные товары для дома и семьи',

    // Hero
    'hero.title': 'Профессиональные инструменты',
    'hero.subtitle': 'Качество без компромиссов',
    'hero.cta': 'Все товары',

    'hero.badge': 'Качественные товары по выгодным ценам',
    'hero.headlineLead': 'Высокий стандарт.',
    'hero.headlineAccent': 'Безопасный выбор.',
    'hero.headlineTail': '',
    'hero.description': 'Каждый товар тщательно отобран для качества, надёжности и реального использования — с сервисом и гарантией после покупки.',
    'hero.badge1': 'Полная гарантия',
    'hero.badge2': 'Доступная поддержка',
    'hero.badge3': 'Безопасная покупка',
    'hero.primaryCta': 'Смотреть товары',
    'hero.secondaryCta': 'Связаться',

    'hero.feature1Title': 'Инструменты',
    'hero.feature1Desc': 'Отвёртки, наборы, измерительные приборы',
    'hero.feature2Title': 'Столярные работы',
    'hero.feature2Desc': 'Столы, шкафы и мебель на заказ',

    // Home
    'home.featuredTitle': 'Популярные товары',
    'home.featuredSubtitle': 'Наши самые востребованные инструменты',
    'home.viewAll': 'Все товары',
    'home.viewAllCategories': 'Все категории',
    'home.ctaTitle': 'Готовы начать?',
    'home.ctaDescription': 'Присоединяйтесь к тысячам довольных клиентов: качественные инструменты, выгодные цены, широкий выбор и доставка по всей стране.',
    'home.ctaButton': 'Начать покупки',
    'home.contactComfort': 'Нам важен ваш комфорт',
    'home.contactAvailable': 'Мы рады ответить на любой вопрос',
    'home.contactUs': 'Свяжитесь с нами',

    // Carpentry section on Index
    'home.contactCta': 'Связаться',
    'home.portfolioBadge': '✦ Портфолио работ ✦',
    'home.carpentryTitle': 'Столярные работы',
    'home.carpentryAccent': 'на заказ',
    'home.carpentryDesc': 'Столы, комоды, тумбы, консоли, табуреты и многое другое — всё из массива дерева, ручная работа.',
    'home.carpentryDescAccent': 'Расскажите, что вам нужно, и мы это построим.',
    'home.tag1': '100% массив дерева',
    'home.tag2': 'Ручная работа',
    'home.tag3': 'Цвет на выбор',
    'home.tag4': 'Размеры на заказ',
    'home.labelOutdoorTable': 'Уличный стол',
    'home.labelTvCabinet': 'Тумба под ТВ',
    'home.labelVanity': 'Тумба под раковину',
    'home.labelShelf': 'Полка',
    'home.labelCoffeeTable': 'Журнальный стол',
    'home.labelGlassCabinet': 'Витрина',
    'home.labelConsole': 'Консоль',
    'home.beforeAfterTitle': 'Реставрация —',
    'home.beforeAfterAccent': 'До и после',
    'home.tableRenovation': 'Реставрация стола',
    'home.deckRenovation': 'Реставрация террасы',
    'home.viewAllWorks': 'Смотреть все работы',

    // Nav links
    'nav.carpentry': 'Наши работы',
    'nav.woodTable': 'Уличный стол',

    // Footer
    'footer.ourWorks': 'Наши работы',
    'footer.outdoorTable': 'Уличный стол',

    // Car Cover Video Section
    'carCover.badge': 'Наш флагман',
    'carCover.videoTitle': 'Автоматический чехол для автомобиля',
    'carCover.videoDescription': 'Смотрите, как чехол автоматически открывается и закрывается, защищая ваш автомобиль в любую погоду',
    'carCover.viewProduct': 'Посмотреть товар',
    'carCover.bestseller': 'Бестселлер',
    'carCover.feature1': 'Автоматическое открытие пультом',
    'carCover.feature2': 'Защита от солнца и дождя',
    'carCover.feature3': 'Простая и быстрая установка',
    'carCover.feature4': '12 вариантов на выбор',

    // Header
    'header.promoBanner': '✦ Мы рады помочь – чувствуйте себя как дома ✦',

    // Navigation
    'navigation.back': 'Назад',
    'navigation.forward': 'Вперёд',

    // Referral
    'referral.title': '🎁 Приведи друга - получи скидку 30₪!',
    'referral.step1': 'Пригласите друга зарегистрироваться с вашим кодом',
    'referral.step2': 'Друг делает покупку и вводит код',
    'referral.step3': 'Друг пишет отзыв на 5 звёзд',
    'referral.reward': '✨ Вы получаете купон на 30₪ прямо на сайте!',
    'referral.myCode': 'Мой реферальный код',
    'product.optionsNotAvailable': 'Эта комбинация недоступна',
    'product.selectOtherOptions': 'Пожалуйста, выберите другую комбинацию',

    // Products
    'products.title': 'Наши товары',
    'products.description': 'Широкий выбор инструментов по лучшим ценам в стране. Удобные и простые покупки с доставкой по всей стране.',
    'products.addToCart': 'В корзину',
    'products.writeReview': 'Написать отзыв',
    'products.viewDetails': 'Подробнее',
    'products.price': 'Цена',
    'products.includesVat': 'Включая НДС',
    'products.outOfStock': 'Нет в наличии',
    'products.clearFilter': 'Сбросить фильтр',
    'products.hotDeals': '🔥 Горячие предложения 🔥',
    'products.startingFrom': 'От ',
    'products.selectOptions': 'Выбрать опции',
    'products.selectOptionsToast': 'Выберите опции (цвет/размер) для точной цены',
    'products.savings': 'Экономия',
    'products.premium': 'Премиум',
    'products.reviews': 'Отзывы',
    'products.viewOptions': 'Варианты',
    'product.allDetails': 'Все детали',

    // Product grid
    'productGrid.emptyTitle': 'Нет товаров для отображения',
    'productGrid.emptyDescription': 'Товары ещё не добавлены. Скажите, что вы хотите продавать, и мы добавим товары для вас.',
    'productGrid.loadMore': 'Загрузить ещё',
    'productGrid.loading': 'Загрузка...',
    'productGrid.showingProducts': 'Показано {count} товаров',
    'productGrid.endOfCatalog': 'Вы достигли конца каталога',
    'productGrid.moreToDiscover': 'Ещё больше отличных товаров',

    // Product detail
    'product.notFound': 'Товар не найден',
    'product.backToProducts': 'Назад к товарам',
    'product.inStock': 'В наличии',
    'product.descriptionTitle': 'Описание товара',
    'product.options': 'Варианты',
    'product.quantity': 'Количество',
    'product.shippingTitle': 'Доставка по стране',
    'product.shippingSubtitle': 'Отправляем прямо к вам',
    'product.fastShipping': 'Доставка по стране',
    'product.shippingDays': 'Прямо к вашей двери',
    'product.securePayment': 'Безопасная оплата',
    'product.sslEncrypted': 'SSL шифрование',
    'product.bestPrice': 'Лучшая цена',
    'product.winningPrice': 'Самая низкая в Израиле',
    'product.personalService': 'Личный сервис',
    'product.fastSupport': 'Быстрая поддержка',

    // Cart
    'cart.title': 'Корзина',
    'cart.empty': 'Ваша корзина пуста',
    'cart.emptyPrompt': 'Добавьте товары в корзину, чтобы начать',
    'cart.added': 'Добавлено в корзину',
    'cart.total': 'Итого',
    'cart.checkout': 'Оформить заказ',
    'cart.processing': 'Обработка...',
    'cart.items': 'товаров в корзине',
    'cart.referralCode': 'Код реферала',
    'cart.referralCodePlaceholder': 'Введите код от друга',
    'cart.validCode': 'Код действителен!',
    'cart.invalidCode': 'Код не найден',
    'cart.redirecting': 'Переход к оплате...',
    'cart.checkoutError': 'Ошибка при оформлении',

    // Contact
    'contact.title': 'Связаться с нами',
    'contact.subtitle': 'Есть вопрос? Мы здесь, чтобы помочь.',
    'contact.formTitle': 'Напишите нам',
    'contact.infoTitle': 'Контактная информация',
    'contact.name': 'Полное имя',
    'contact.namePlaceholder': 'Ваше имя',
    'contact.email': 'Эл. почта',
    'contact.phone': 'Телефон',
    'contact.subject': 'Тема',
    'contact.subjectPlaceholder': 'Чем мы можем помочь?',
    'contact.message': 'Сообщение',
    'contact.messagePlaceholder': 'Расскажите подробнее...',
    'contact.send': 'Отправить',
    'contact.sending': 'Отправка...',
    'contact.success': 'Сообщение отправлено!',
    'contact.hours': 'Часы работы',
    'contact.hoursContent': 'Вс-Чт: 09:00-18:00 | Пт: 09:00-13:00',
    'contact.consentText': 'Я согласен на сбор и использование моей информации в соответствии с',
    'contact.privacyLink': 'Политикой конфиденциальности',
    'contact.consentRequired': 'Необходимо принять политику конфиденциальности',
    'contact.error': 'Ошибка отправки сообщения',
    'contact.errorDescription': 'Пожалуйста, попробуйте позже',

    // Footer
    'footer.description': 'Широкий выбор инструментов по отличным ценам. Удобный заказ и доставка по всей стране.',
    'footer.rights': 'Все права защищены',
    'footer.quickLinks': 'Быстрые ссылки',
    'footer.contact': 'Контакты',
    'footer.newsletterTitle': 'Будьте в курсе',
    'footer.newsletterSubtitle': 'Подпишитесь на рассылку и получайте эксклюзивные скидки',
    'footer.emailPlaceholder': 'Ваш email',
    'footer.subscribe': 'Подписаться',
    'footer.privacy': 'Политика конфиденциальности',
    'footer.terms': 'Условия использования',
    'footer.exemptDealer': 'Освобождённый дилер - Цена не включает НДС',

    // Privacy
    'privacy.title': 'Политика конфиденциальности',
    'privacy.lastUpdated': 'Последнее обновление',
    'privacy.intro.title': 'Введение',
    'privacy.intro.content': 'Мы уважаем вашу конфиденциальность и обязуемся защищать вашу личную информацию. Эта политика объясняет, как мы собираем, используем и защищаем вашу информацию.',
    'privacy.collection.title': 'Информация, которую мы собираем',
    'privacy.collection.intro': 'Мы можем собирать следующую информацию:',
    'privacy.collection.item1': 'Личные данные: имя, адрес электронной почты, номер телефона',
    'privacy.collection.item2': 'Данные просмотра: IP-адрес, тип браузера, посещённые страницы',
    'privacy.collection.item3': 'Информация о заказах: история покупок, предпочтения',
    'privacy.collection.item4': 'Техническая информация: файлы cookie и данные об использовании',
    'privacy.purpose.title': 'Цель сбора',
    'privacy.purpose.item1': 'Обработка заказов и доставка',
    'privacy.purpose.item2': 'Улучшение пользовательского опыта на сайте',
    'privacy.purpose.item3': 'Отправка обновлений и предложений (с вашего согласия)',
    'privacy.purpose.item4': 'Ответы на запросы и обслуживание клиентов',
    'privacy.security.title': 'Безопасность данных',
    'privacy.security.content': 'Мы применяем передовые меры безопасности для защиты вашей информации, включая шифрование, ограниченный доступ и безопасное хранение. Однако ни один метод передачи через Интернет не является на 100% безопасным.',
    'privacy.cookies.title': 'Файлы cookie',
    'privacy.cookies.content': 'Сайт использует файлы cookie для улучшения опыта просмотра, анализа трафика и персонализации. Вы можете управлять настройками cookie через браузер.',
    'privacy.thirdParty.title': 'Передача данных третьим лицам',
    'privacy.thirdParty.content': 'Ваша информация может быть передана внешним поставщикам услуг (таким как службы доставки и обработки платежей) для выполнения заказа. Некоторые поставщики могут находиться за пределами Израиля.',
    'privacy.rights.title': 'Права пользователя',
    'privacy.rights.intro': 'В соответствии с законом о конфиденциальности у вас есть следующие права:',
    'privacy.rights.item1': 'Доступ к собранной о вас информации',
    'privacy.rights.item2': 'Запрос на исправление или удаление информации',
    'privacy.rights.item3': 'Отказ от маркетинговых сообщений',
    'privacy.rights.contact': 'Для запросов относительно вашей личной информации свяжитесь с нами по',
    'privacy.contact.title': 'Связаться с нами',
    'privacy.contact.content': 'По вопросам о нашей политике конфиденциальности, пожалуйста, свяжитесь с нами:',

    // Terms
    'terms.title': 'Условия использования',
    'terms.intro.title': 'Общие положения',
    'terms.intro.content': 'Добро пожаловать на наш сайт. Использование этого сайта означает согласие с этими условиями. Пожалуйста, внимательно прочитайте их.',
    'terms.usage.title': 'Правила использования',
    'terms.usage.item1': 'Сайт предназначен только для личного, некоммерческого использования',
    'terms.usage.item2': 'Не используйте сайт способом, который может нанести вред его работе или другим пользователям',
    'terms.usage.item3': 'Не копируйте, не дублируйте и не распространяйте контент сайта без письменного разрешения',
    'terms.ip.title': 'Интеллектуальная собственность',
    'terms.ip.content': 'Весь контент сайта, включая изображения, тексты, логотипы и дизайн, защищён авторским правом и является нашей исключительной собственностью.',
    'terms.liability.title': 'Ограничение ответственности',
    'terms.liability.content': 'Сайт предоставляется «как есть». Мы не несём ответственности за косвенные или последующие убытки, возникающие в результате использования сайта или продаваемых на нём товаров.',
    'terms.purchases.title': 'Покупки и доставка',
    'terms.purchases.content': 'Условия покупки:',
    'terms.purchases.item1': 'Цены включают НДС, если не указано иное',
    'terms.purchases.item2': 'Доставка осуществляется по всей стране. Сроки доставки могут варьироваться',
    'terms.purchases.item3': 'Проверьте товар при получении и немедленно сообщите о любых дефектах',
    'terms.cancellation.title': 'Отмена и возврат',
    'terms.cancellation.content': 'Заказы можно отменить в соответствии с законом о защите прав потребителей в течение 14 дней с момента получения товара. Товар должен быть возвращён в оригинальной упаковке и в хорошем состоянии. Могут применяться сборы за доставку и обработку.',
    'terms.jurisdiction.title': 'Юрисдикция',
    'terms.jurisdiction.content': 'Эти условия регулируются законами Государства Израиль. Любой спор рассматривается в компетентных судах Израиля.',
    'terms.contact.title': 'Связаться с нами',
    'terms.contact.content': 'По вопросам об этих условиях использования, пожалуйста, свяжитесь с нами:',

    // Cookies consent
    'cookies.title': 'Этот сайт использует cookie',
    'cookies.description': 'Мы используем файлы cookie для улучшения вашего опыта, анализа трафика и персонализации контента.',
    'cookies.learnMore': 'Узнать больше',
    'cookies.accept': 'Принять',
    'cookies.decline': 'Отклонить',

    // About
    'about.title': 'О нас',

    // Auth
    'auth.welcome': 'Добро пожаловать',
    'auth.description': 'Войдите или зарегистрируйтесь, чтобы получить доступ к личному кабинету',
    'auth.login': 'Вход',
    'auth.signup': 'Регистрация',
    'auth.email': 'Эл. почта',
    'auth.password': 'Пароль',
    'auth.fullName': 'Полное имя',
    'auth.fullNamePlaceholder': 'Введите ваше полное имя',
    'auth.loginButton': 'Войти',
    'auth.signupButton': 'Зарегистрироваться',
    'auth.logout': 'Выйти',
    'auth.loginSuccess': 'Вход выполнен успешно!',
    'auth.signupSuccess': 'Регистрация прошла успешно!',
    'auth.logoutSuccess': 'Вы вышли из системы',
    'auth.invalidCredentials': 'Неверный email или пароль',
    'auth.alreadyRegistered': 'Пользователь уже зарегистрирован',
    'auth.error': 'Ошибка, попробуйте ещё раз',
    'auth.passwordTooShort': 'Пароль должен содержать минимум 6 символов',
    'auth.passwordHint': 'Минимум 6 символов',
    'auth.referralCode': 'Реферальный код (необязательно)',
    'auth.referralCodePlaceholder': 'Введите реферальный код',
    'auth.referralCodeHint': 'Если вы получили код от друга, введите его здесь',
    'auth.googleSignIn': 'Войти через Google',
    'auth.googleSignUp': 'Зарегистрироваться через Google',
    'auth.orContinueWith': 'или продолжить с',

    // Profile
    'profile.welcome': 'Привет',
    'profile.referralProgram': 'Программа «Приведи друга»',
    'profile.referralDescription': 'Поделитесь кодом с друзьями и получите скидку 30₪ за каждого друга, который купит и оставит отзыв!',
    'profile.yourCode': 'Ваш код',
    'profile.shareLink': 'Ссылка для друзей',
    'profile.copy': 'Копировать',
    'profile.linkCopied': 'Ссылка скопирована!',
    'profile.codeCopied': 'Код скопирован!',
    'profile.howItWorks': 'Как это работает?',
    'profile.step1': 'Поделитесь ссылкой или кодом с друзьями',
    'profile.step2': 'Друг регистрируется с вашим кодом',
    'profile.step3': 'Друг делает покупку и вводит код',
    'profile.step4': 'Друг оставляет отзыв на 5 звёзд с кодом — и вы получаете 30₪!',
    'profile.totalReferrals': 'Всего рефералов',
    'profile.pendingReferrals': 'Ожидают',
    'profile.completedReferrals': 'Завершены',
    'profile.referralStatus': 'Статус рефералов',
    'profile.rewardSent': 'Награда отправлена!',
    'profile.inProgress': 'В процессе',
    'profile.purchaseStep': 'Покупка',
    'profile.reviewStep': 'Отзыв',
    'profile.yourCoupon': 'Ваш купон',

    // Coupon Modal
    'coupon.congratulations': 'Поздравляем!',
    'coupon.earnedMessage': 'Ваш друг выполнил все условия - вы получили купон!',
    'coupon.yourCode': 'Ваш код купона:',
    'coupon.discount': 'скидка',
    'coupon.useInstructions': 'Введите этот код при оформлении заказа',
    'coupon.gotIt': 'Понятно!',
    'profile.earnedCoupons': 'Ваши заработанные купоны',
    'profile.couponFrom': 'Купон от',

    // Nav
    'nav.profile': 'Мой кабинет',
    'nav.login': 'Вход',

    // Reviews
    'reviews.title': 'Отзывы',
    'reviews.count': 'отзывов',
    'reviews.writeReview': 'Написать отзыв',
    'reviews.rating': 'Рейтинг',
    'reviews.content': 'Содержание отзыва',
    'reviews.contentPlaceholder': 'Поделитесь своим опытом с этим товаром...',
    'reviews.referralCode': 'Реферальный код',
    'reviews.referralCodePlaceholder': 'Введите ваш реферальный код',
    'reviews.referralHint': '5-звездочный отзыв с реферальным кодом дает вам кредит!',
    'reviews.submit': 'Отправить отзыв',
    'reviews.submitted': 'Отзыв отправлен!',
    'reviews.approvedNow': 'Ваш отзыв опубликован',
    'reviews.pendingApproval': 'Ваш отзыв на модерации',
    'reviews.referralCredit': 'Вы получили реферальный кредит! 🎉',
    'reviews.submitError': 'Ошибка отправки отзыва',
    'reviews.alreadyReviewed': 'Вы уже оставляли отзыв на этот товар',
    'reviews.loginToReview': 'Войдите, чтобы написать отзыв',
    'reviews.noReviews': 'Пока нет отзывов. Будьте первым!',
    'reviews.anonymous': 'Анонимный пользователь',

    // Showcase (Featured Product)
    'showcase.badge': 'Рекомендуемый товар',
    'showcase.title1': 'Электрический камин',
    'showcase.title2': 'Роскошный и современный',
    'showcase.description': 'Аутентичный огонь без дыма и опасности. Полный контроль через умное приложение, настройка цвета пламени и интенсивности тепла. Доступен в различных размерах для идеального соответствия любому пространству.',
    'showcase.feature1': 'Реалистичное пламя',
    'showcase.feature2': 'Энергоэффективный',
    'showcase.feature3': 'Управление приложением',
    'showcase.cta': 'Посмотреть товар',
    'showcase.blueFlame': 'Синее пламя',
    'showcase.orangeFlame': 'Оранжевое пламя',
    'showcase.appControl': 'Управление приложением',

    // Hot Deals Section
    'hotDeals.badge': 'Ограниченное предложение',
    'hotDeals.title': 'Недельные акции',
    'hotDeals.description': 'Эксклюзивные скидки на избранные товары - пока есть в наличии',
    'hotDeals.days': 'Дней',
    'hotDeals.hours': 'Часов',
    'hotDeals.minutes': 'Минут',
    'hotDeals.seconds': 'Секунд',
    'hotDeals.endsAt': 'Акция заканчивается 20 января 2026',
    'hotDeals.saleBadge': 'Акция',
    'hotDeals.viewAll': 'Все акции',

    // Testimonials Section
    'testimonials.badge': 'Отзывы клиентов',
    'testimonials.title': 'Довольные покупатели',
    'testimonials.description': 'Тысячи клиентов уже пользуются нашими товарами - присоединяйтесь!',
    'testimonials.verified': 'Проверено',

    // General
    'language': 'Язык',

    // Shipping Countries
    'shipping.availableCountries': 'Доступные страны доставки',
    'shipping.weShipTo': 'Мы отправляем только в эти страны',
    'shipping.israel': 'Израиль',
    'shipping.usa': 'США',
    'shipping.japan': 'Япония',
    'shipping.italy': 'Италия',

    // Trust Section
    'trust.badge': 'Безопасная оплата',
    'trust.title': 'Безопасные покупки',
    'trust.subtitle': 'Обработка карт через PayPlus и Isracard – ваши данные зашифрованы и не хранятся на сайте.',
    'trust.pciTitle': 'Стандарт PCI-DSS',
    'trust.pciDesc': 'Высший стандарт безопасности платежей',
    'trust.sslTitle': 'Шифрование SSL/TLS',
    'trust.sslDesc': 'Данные зашифрованы и идут напрямую в банк',
    'trust.3dsTitle': '3D Secure',
    'trust.3dsDesc': 'Проверка владельца карты для защиты от мошенничества',
    'trust.israelcardTitle': 'PayPlus + Isracard',
    'trust.israelcardDesc': 'Ведущая платёжная система Израиля',

    // Hero - Updated messaging
    'hero.headlineTagline': 'Высокий стандарт. Безопасный выбор.',
    'hero.whyBuyCta': 'Почему клиенты выбирают нас?',

    // Why Buy From Us page
    'whyBuy.pageTitle': 'Почему покупать у нас?',
    'whyBuy.pageSubtitle': 'Каждый товар на сайте тщательно отобран для обеспечения качества, надёжности и реальной пользы — с сервисом и гарантией, которые сопровождают вас и после покупки.',
    'whyBuy.qualityTitle': 'Тщательно отобранные качественные товары',
    'whyBuy.qualityDesc': 'Каждый товар выбирается по высоким стандартам качества, надёжности и реального использования — не количество, а уровень.',
    'whyBuy.warrantyTitle': 'Гарантия и персональный сервис',
    'whyBuy.warrantyDesc': 'Мы стоим за нашими товарами и оказываем поддержку даже после покупки, чтобы вы чувствовали уверенность на каждом шагу.',
    'whyBuy.secureTitle': 'Безопасная покупка',
    'whyBuy.secureDesc': 'Процесс покупки защищён и безопасен, чтобы вы могли заказывать с полной уверенностью.',
    'whyBuy.experienceTitle': 'Простой и понятный опыт покупок',
    'whyBuy.experienceDesc': 'Понятная информация, удобная навигация и простой выбор — приятный опыт покупок без беспорядка и путаницы.',
    'whyBuy.needHelp': 'Нужна помощь?',
    'whyBuy.helpDescription': 'По любому вопросу или консультации вы можете позвонить нам:',
    'whyBuy.happyToServe': 'Мы рады вам помочь',
    'whyBuy.startShopping': 'Начать покупки',
    'whyBuy.productsShowcase': 'Избранные товары',
    'whyBuy.trustBadge1': 'Высокое качество',
    'whyBuy.trustBadge2': 'Довольные клиенты',
    'whyBuy.trustBadge3': 'Профессиональный сервис',
  },
};

export const useLanguageStore = create<LanguageStore>()(
  persist(
    (set, get) => ({
      language: 'he', // Default to Hebrew
      // Arabic is deprecated/removed from UI; normalize to Hebrew if encountered.
      setLanguage: (language) => set({ language: language === 'ar' ? 'he' : language }),
      t: (key: string) => {
        const { language } = get();
        return translations[language][key] || translations['he'][key] || key;
      },
    }),
    {
      name: 'language-store',
      // Ensure Hebrew is the default when no stored value exists
      onRehydrateStorage: () => (state) => {
        if (state && !state.language) {
          state.language = 'he';
        }

        // If a user had Arabic stored previously, force Hebrew.
        if (state?.language === 'ar') {
          state.language = 'he';
        }
      },
    }
  )
);

// Helper to get RTL languages
export const isRTL = (lang: Language) => lang === 'he';

// Language display names
export const languageNames: Record<Language, string> = {
  he: 'עברית',
  en: 'English',
  ar: 'العربية',
  ru: 'Русский',
};
